import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, ApiError, isNetworkError } from "../api";
import { warnIfShared } from "../grantwarn";
import { viewFromSlug, VIEW_TO_SLUG } from "../views";
import { detailScreen } from "../appdetail";
import { customLimitError, limitInputs, limitsPatchBody, sizeFromMB, sizeToMB } from "../limits";
import { visibilityChanges } from "../visibility";
import { retryChunk } from "../chunkreload";
import { useDropdown, useReconnect } from "../hooks";
import { ConnIcon, CopyButton, CopyIcon, DocsLink, WarnIcon, ErrorBanner, Loading, Snippet, StatusDot, VisibilityBadge, VisibilityChoice, pairMB, usageLevel, UsagePair, cores, visibilityOf, useEscape } from "../components";
import { resolveTabs, normalizeTabs, tabsFromCsv, tabsToCsv, TOGGLEABLE_TABS, TAB_LABELS } from "../tabs";
import { useSetAppHeader } from "../appHeader";

// xterm is heavy and only needed when a terminal is actually opened, so it is
// split into its own chunk. The loaders are named so the page can prefetch every
// tab's chunk eagerly (see prefetchTabs), while lazy() still handles rendering.
// Wrapped in retryChunk: a deploy replaces every chunk's content hash, so a tab
// opened before one holds names that no longer exist. Without this the first
// tab switch after a deploy fails with "error loading dynamically imported
// module" and the only cure is a reload the person has to think of.
const importTerminal = retryChunk(() => import("./AppTerminal"));
const importAssistant = retryChunk(() => import("./AppAssistant"));
const importEditor = retryChunk(() => import("./AppEditor"));
const importLogs = retryChunk(() => import("./AppLogs"));
const AppTerminal = lazy(importTerminal);
// The assistant pulls in a markdown renderer, so it stays a lazy chunk too.
const AppAssistant = lazy(importAssistant);
// The file-tree editor is its own view, loaded on demand when selected.
const AppEditor = lazy(importEditor);
const AppLogs = lazy(importLogs);

// The toggleable app-detail views, mapped to and from the tab keys the profile
// and per-app override speak (tabs.js). "editor" is the internal name of the
// "files" tab. The always-on views (snapshots, connections, settings) are not
// in these maps, so they are never hidden by a tab preference.
const VIEW_TO_TABKEY = { assistant: "assistant", editor: "files", terminal: "terminal", logs: "logs" };
const TABKEY_TO_VIEW = { assistant: "assistant", files: "editor", terminal: "terminal", logs: "logs" };

// tabChunkLoaders maps a view to its code-chunk loader, for eager prefetching.
const tabChunkLoaders = {
  terminal: importTerminal,
  assistant: importAssistant,
  editor: importEditor,
  logs: importLogs,
};

// prefetchTabs warms every tab's code chunk so switching tabs is instant, loading
// the given (active) tab first and the rest right after. It fetches code only; a
// tab still mounts and does its own data loading when actually opened, so eager
// prefetching never opens a terminal session or starts polling on its own.
const prefetchTabs = (active) => {
  const load = (v) => tabChunkLoaders[v] && tabChunkLoaders[v]();
  Promise.resolve(load(active)).finally(() => {
    Object.keys(tabChunkLoaders)
      .filter((v) => v !== active)
      .forEach(load);
  });
};

// The SPA is served by the hostit daemon itself, so the agent API lives on our
// own origin under /api.
const origin = window.location.origin;
const tokenPlaceholder = "<this app has no agent token>";

// The chat/preview split remembers how the owner sized it, across apps and reloads.
const splitKey = "hostit.ws.chatFrac";
const defaultChatFrac = 0.52;

// How a lifecycle action is watched to completion. `done(app, base)` decides when
// the transition has actually happened: `base` is the app's start times captured
// at the click. Reboot and restart end in the same running state they began in, so
// they wait for a start time strictly newer than the baseline; the others just
// wait for the container or app process to flip. Power/reboot bring the whole app
// back up (the agent starts the run: command), so their target is app_running.
const TRANSITIONS = {
  poweron: { label: "Powering on", done: (a) => a.running && a.app_running },
  poweroff: { label: "Powering off", done: (a) => !a.running },
  reboot: { label: "Rebooting", done: (a, b) => a.running && a.app_running && a.started_at > b.started_at },
  start: { label: "Starting app", done: (a) => a.running && a.app_running },
  stop: { label: "Stopping app", done: (a) => a.running && !a.app_running },
  restart: { label: "Restarting app", done: (a, b) => a.running && a.app_running && a.app_started_at > b.app_started_at },
};
const transitionPollMs = 2000;
const transitionTimeoutMs = 90000;
// The detail page shows live gauges, so it refreshes faster than the dashboard.
// The daemon serves state from a warm cache, so this stays cheap.
const detailPollMs = 8000;

// The whole point of the prompt: a short, ready-to-paste block that points an
// external agent at the app's own info endpoint. Everything the agent needs to
// know about the API is returned by that call, so the prompt stays small and does
// not duplicate it -- it only sets the agent's stance: learn the API, then wait
// for the owner rather than interrogating them or building on a guess.
//
// Two shapes, because they are two different jobs. A stub is an invitation to
// build. An app whose agent has written a description into hostit.yml is finished
// work someone is coming back to: say that up front, or the next session reads a
// "build me an app" prompt and starts over on top of it.
const promptText = (name, url, token, existing) => {
  const apiLine = `Manage it through the hostit REST API: call ${origin}/api/apps/${name}/info with the Bearer token ${token} to learn how, then follow what it returns.`;
  if (!existing) {
    return `I've created a hostit app "${name}" at ${url}.

${apiLine}

Read that, then reply exactly: "I understand the hostit API. Tell me what you want to build." Don't ask exploratory questions or build anything until I do.
`;
  }
  return `I'm continuing work on my hostit app "${name}" at ${url}.

${apiLine}

Read that plus the app's README.md and docs/, then reply exactly: "I understand this app. Tell me what you want to change." Don't rebuild it from scratch or change anything until I do.
`;
};

// A small svg icon set, so the top bar reads as buttons, not a wall of words.
const TerminalIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
    <path d="M4 6l2.2 2L4 10M8.5 10.5H11.5" />
  </svg>
);
const BackIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 3.5 5 8l4.5 4.5" />
  </svg>
);
const SparkleIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 0.8l1.5 4.2 4.2 1.5-4.2 1.5L8 12.2 6.5 8 2.3 6.5 6.5 5z" />
    <path d="M13 9.5l0.6 1.6 1.6 0.6-1.6 0.6L13 14l-0.6-1.7-1.6-0.6 1.6-0.6z" />
  </svg>
);

// Small monochrome glyphs for the dropdown menu items.
const mi = (paths) => () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const PlayIcon = mi(<path d="M5 3.5v9l7-4.5z" />);
const StopIcon = mi(<rect x="4" y="4" width="8" height="8" rx="1" />);
const RestartIcon = mi(<><path d="M13 8a5 5 0 1 1-1.5-3.5" /><path d="M13 3v2.2h-2.2" /></>);
const RebootIcon = mi(<><path d="M3.2 8a4.8 4.8 0 1 0 1.4-3.4" /><path d="M4.6 2.3v2.3h2.3" /><path d="M8 5v3" /></>);
const PowerIcon = mi(<><path d="M8 2.2v5" /><path d="M4.6 4.6a4.6 4.6 0 1 0 6.8 0" /></>);
const TrashIcon = mi(<><path d="M3 4.5h10" /><path d="M6.5 4.5V3h3v1.5" /><path d="M4.5 4.5l.6 8.5a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8.5" /></>);
const ArchiveIcon = mi(<><path d="M2.5 4.5h11" /><path d="M3.5 4.5v8a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-8" /><path d="M6.5 7.5h3" /></>);
const ForkIcon = mi(<><circle cx="4.5" cy="4" r="1.6" /><circle cx="11.5" cy="4" r="1.6" /><circle cx="8" cy="12.5" r="1.6" /><path d="M4.5 5.6v1.4a2 2 0 0 0 2 2H8m3.5-3.4v1.4a2 2 0 0 1-2 2H8m0 0v1.9" /></>);
const DownloadIcon = mi(<><path d="M8 2v7.5" /><path d="M5 6.5 8 9.5l3-3" /><path d="M3 12.5h10" /></>);
const DotsIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="8" cy="3.1" r="1.35" />
    <circle cx="8" cy="8" r="1.35" />
    <circle cx="8" cy="12.9" r="1.35" />
  </svg>
);
const KeyIcon = mi(<><circle cx="5" cy="11" r="2.3" /><path d="M6.7 9.3l5-5M10.7 5.3l1.4 1.4M12.4 3.6l1.4 1.4" /></>);
const RollbackIcon = mi(<><path d="M4.4 5.2A4.7 4.7 0 1 1 3.4 8.2" /><path d="M1.9 2.9v2.7h2.7" /></>);

// CopyMini is a small two-document copy icon that copies one value and briefly shows
// a check. Used next to each DNS record field so an owner can copy just the name or
// just the value into their DNS provider.
const CopyMini = ({ text, label }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="copy-mini"
      title={label || "Copy"}
      aria-label={label || "Copy"}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {done ? (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5.5" y="5.5" width="8" height="8.5" rx="1.3" />
          <path d="M3.5 10.5V3.2a1 1 0 0 1 1-1H10" />
        </svg>
      )}
    </button>
  );
};

// formatUptime turns a container start time (Unix seconds) into a short duration
// like "3d 4h", "2h 15m", "8m" or "42s"; a stopped container has no uptime.
const formatUptime = (startedAt) => {
  if (!startedAt) {
    return "-";
  }
  let s = Math.max(0, Math.floor(Date.now() / 1000 - startedAt));
  const d = Math.floor(s / 86400);
  s -= d * 86400;
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

// CPU / RAM / Disk as the same compact icon pairs the rest of the UI uses --
// no dots, no bars; severity is the shared text colour. Only meaningful while
// the container is up, so the caller shows it only then.
const UsageGrid = ({ app }) => {
  const cpuPct = app.cpu_percent || 0;
  const cpuLevel = cpuPct >= 90 ? "crit" : cpuPct >= 75 ? "warn" : "";
  return (
    <div className="ws-resources">
      <span className={"usage-item usage-" + cpuLevel} title={app.cpu_milli ? `CPU use; capped at ${cores(app.cpu_milli / 1000)}` : "CPU use"}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="4" width="8" height="8" rx="1" />
          <path d="M6 1.5V4M10 1.5V4M6 12v2.5M10 12v2.5M1.5 6H4M1.5 10H4M12 6h2.5M12 10h2.5" />
        </svg>
        {cpuPct}%
      </span>
      <UsagePair kind="ram" used={app.memory_mb} total={app.memory_limit_mb} />
      <UsagePair kind="disk" used={app.disk_mb} total={app.disk_limit_mb} />
    </div>
  );
};

// A small dropdown wrapper shared by the Actions and Terminal menus: closes on an
// outside click or Escape.

// A split button for the terminal: the left half (the icon) opens the web shell
// directly; the right half (the caret) drops a menu (web shell / SSH). When a
// shell session is running -- even minimized out of sight -- the button goes dark
// so the owner can see it is still there, and the icon click brings it back.
const TerminalSplitButton = ({ active, connecting, onWebShell, onSsh }) => {
  const { open, setOpen, ref } = useDropdown();
  const pick = (fn) => {
    setOpen(false);
    fn();
  };
  const cls = ["menu split-btn", active ? "split-btn-active" : "", connecting ? "split-btn-loading" : ""]
    .join(" ")
    .trim();
  return (
    <div className={cls} ref={ref}>
      <button
        type="button"
        className="btn split-btn-main"
        onClick={onWebShell}
        disabled={connecting}
        title={connecting ? "Connecting..." : active ? "Show web shell" : "Open web shell"}
        aria-label={connecting ? "Connecting" : active ? "Show web shell" : "Open web shell"}
      >
        <TerminalIcon />
      </button>
      <button
        type="button"
        className="btn split-btn-caret"
        onClick={() => setOpen(!open)}
        disabled={connecting}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Terminal options"
      >
        <span aria-hidden="true">&#9662;</span>
      </button>
      {open && (
        <div className="menu-items" role="menu">
          <MenuItem icon={<TerminalIcon />} label="Web shell" onClick={() => pick(onWebShell)} />
          <MenuItem icon={<KeyIcon />} label="Connect via SSH" onClick={() => pick(onSsh)} />
        </div>
      )}
    </div>
  );
};

// MenuItem is one dropdown row: a small leading icon and a label.
const MenuItem = ({ icon, label, onClick, disabled, danger, title }) => (
  <button
    type="button"
    role="menuitem"
    className={danger ? "menu-item-danger" : undefined}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    <span className="menu-ico" aria-hidden="true">
      {icon}
    </span>
    {label}
  </button>
);

// The export endpoint returns a .zip by default; ?format=tar switches it to a
// gzipped tarball. tarHref appends that once so the two download menus stay in sync.
const tarHref = (base) => `${base}?format=tar`;

// DownloadMenu is a download icon that drops a two-item menu: grab the target
// (the live workspace, or one snapshot) as a .zip or a .tar.gz. base is the
// export URL; the tar variant just adds ?format=tar. Plain <a download> links so
// the browser streams the archive straight to disk (it can be large), the session
// cookie authenticating the GET.
const DownloadMenu = ({ base, small, className, title = "Download" }) => {
  const { open, setOpen, ref } = useDropdown();
  const btnCls = ["btn", "btn-icon", small ? "btn-small" : ""].filter(Boolean).join(" ");
  const wrapCls = ["menu", className].filter(Boolean).join(" ");
  return (
    <span className={wrapCls} ref={ref}>
      <button type="button" className={btnCls} onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} title={title} aria-label={title}>
        <DownloadIcon />
      </button>
      {open && (
        <div className="menu-items" role="menu">
          <a role="menuitem" href={base} download onClick={() => setOpen(false)}>
            <span className="menu-ico" aria-hidden="true"><DownloadIcon /></span>
            Download as .zip
          </a>
          <a role="menuitem" href={tarHref(base)} download onClick={() => setOpen(false)}>
            <span className="menu-ico" aria-hidden="true"><ArchiveIcon /></span>
            Download as .tar.gz
          </a>
        </div>
      )}
    </span>
  );
};

// MenuDownloadSub is the same two format choices as one expandable row inside an
// existing dropdown -- the form the header download takes once it has collapsed
// into the kebab on a narrow screen, and the per-snapshot download inside a
// snapshot's kebab.
const MenuDownloadSub = ({ base, label }) => {
  const [subOpen, setSubOpen] = useState(false);
  return (
    <>
      <button type="button" role="menuitem" className="menu-sub-toggle" onClick={() => setSubOpen(!subOpen)} aria-haspopup="menu" aria-expanded={subOpen}>
        <span className="menu-ico" aria-hidden="true"><DownloadIcon /></span>
        {label}
        <span className="menu-sub-caret" aria-hidden="true">{subOpen ? "\u25be" : "\u25b8"}</span>
      </button>
      {subOpen && (
        <div className="menu-subitems">
          <a role="menuitem" href={base} download>as .zip</a>
          <a role="menuitem" href={tarHref(base)} download>as .tar.gz</a>
        </div>
      )}
    </>
  );
};

// Everything rare about an app behind one button, grouped: app actions (the run:
// command), container actions (power), settings (token + custom domains), and
// delete -- dividers between the groups. Only one app verb is ever the sensible
// next move, and when the container is off there is no app to act on, so that
// group is dropped.
const MenuPencilIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11.3 2.2l2.5 2.5L6 12.5l-3.2.7.7-3.2z" />
  </svg>
);

const MenuTransferIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 5.5h9M9 3l2.5 2.5L9 8" />
    <path d="M13.5 10.5h-9M7 8l-2.5 2.5L7 13" />
  </svg>
);

const MenuGaugeIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="12" height="8" rx="1" />
    <path d="M4.5 12v2M8 12v2M11.5 12v2M4.5 2v2M8 2v2M11.5 2v2" />
  </svg>
);

const ActionsMenu = ({ running, appRunning, busy, onAction, onDelete, canDelete = true, onSsh, onByoa, onFork, downloadBase, archived, onArchive, onUnarchive, onRename, onTransfer, onResources, isChat = false, onConvert }) => {
  const { open, setOpen, ref } = useDropdown();

  const run = (action) => {
    setOpen(false);
    onAction(action);
  };
  const pick = (fn) => () => {
    setOpen(false);
    fn();
  };
  const noun = isChat ? "conversation" : "app";
  const appVerbs = appRunning
    ? [
        { verb: "restart", label: "Restart app", icon: <RestartIcon /> },
        { verb: "stop", label: "Stop app", icon: <StopIcon /> },
      ]
    : [{ verb: "start", label: "Start app", icon: <PlayIcon /> }];

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="btn btn-icon"
        onClick={() => setOpen(!open)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title={busy ? "Working..." : "Actions"}
        aria-label="Actions"
      >
        <DotsIcon />
      </button>
      {open && isChat && (
        <div className="menu-items" role="menu">
          {/* A conversation's actions: dormancy, rename, convert to an app, and
              delete. No serve/app tools -- it never serves. */}
          {running ? (
            <MenuItem icon={<PowerIcon />} label="Power off" onClick={() => run("poweroff")} />
          ) : (
            <MenuItem icon={<PowerIcon />} label="Power on" onClick={() => run("poweron")} />
          )}
          <div className="menu-sep" />
          <MenuItem icon={<MenuPencilIcon />} label="Rename" onClick={pick(onRename)} disabled={!canDelete} />
          <MenuItem icon={<ForkIcon />} label="Convert to an app" onClick={pick(onConvert)} disabled={!canDelete} title={canDelete ? "Turn this conversation into a hosted app" : "Only the owner can convert this"} />
          <div className="menu-sep" />
          <MenuItem icon={<TrashIcon />} label="Delete conversation" onClick={pick(onDelete)} danger disabled={!canDelete} />
        </div>
      )}
      {open && !isChat && (
        <div className="menu-items" role="menu">
          <div className="menu-narrow-only">
            <MenuItem icon={<SshMenuIcon />} label="Connect via SSH" onClick={pick(onSsh)} />
            <MenuItem icon={<SparkleIcon />} label="Use your own AI agent" onClick={pick(onByoa)} />
            <MenuItem icon={<ForkIcon />} label="Fork app" onClick={pick(onFork)} />
            <MenuDownloadSub base={downloadBase} label="Download workspace" />
            <div className="menu-sep" />
          </div>
          {/* An archived app is offered none of the lifecycle verbs: every one
              of them would be refused, so showing them would only mislead. */}
          {!archived && running && (
            <>
              {appVerbs.map((a) => (
                <MenuItem key={a.verb} icon={a.icon} label={a.label} onClick={() => run(a.verb)} />
              ))}
              <div className="menu-sep" />
            </>
          )}

          {!archived &&
            (running ? (
              <>
                <MenuItem icon={<RebootIcon />} label="Reboot" onClick={() => run("reboot")} />
                <MenuItem icon={<PowerIcon />} label="Power off" onClick={() => run("poweroff")} />
              </>
            ) : (
              <MenuItem icon={<PowerIcon />} label="Power on" onClick={() => run("poweron")} />
            ))}
          <div className="menu-sep" />
          <MenuItem
            icon={<MenuPencilIcon />}
            label="Rename app"
            onClick={pick(onRename)}
            disabled={!canDelete}
            title={canDelete ? undefined : "Only the owner can rename the app"}
          />
          <MenuItem
            icon={<MenuTransferIcon />}
            label="Transfer ownership"
            onClick={pick(onTransfer)}
            disabled={!canDelete}
            title={canDelete ? undefined : "Only the owner can transfer the app"}
          />
          <MenuItem
            icon={<MenuGaugeIcon />}
            label="Change resources"
            onClick={pick(onResources)}
            disabled={!canDelete}
            title={canDelete ? undefined : "Only the owner can change resources"}
          />
          <MenuItem
            icon={<ArchiveIcon />}
            label={archived ? "Unarchive app" : "Archive app"}
            onClick={pick(archived ? onUnarchive : onArchive)}
            disabled={!canDelete}
            title={canDelete ? undefined : "Only the owner can archive the app"}
          />
          <div className="menu-sep" />
          <MenuItem
            icon={<TrashIcon />}
            label="Delete app"
            onClick={pick(onDelete)}
            danger
            disabled={!canDelete}
            title={canDelete ? undefined : "Only the owner can delete the app"}
          />
        </div>
      )}
    </div>
  );
};


// UnknownView answers a URL naming a view that does not exist -- a typo'd
// link, or the internal view name where the public slug belongs. It used to
// fall back silently to the remembered tab, which showed a page that answered
// a different question than the URL asked.
const UnknownView = ({ name, slug }) => (
  <div className="card">
    <h2>No such view</h2>
    <p className="empty">
      There is no <span className="mono">{slug}</span> view. The tabs are{" "}
      <span className="mono">assistant</span>, <span className="mono">files</span>,{" "}
      <span className="mono">terminal</span>, <span className="mono">snapshots</span>,{" "}
      <span className="mono">logs</span> and <span className="mono">settings</span>.
    </p>
    <Link className="btn" to={`/app/${encodeURIComponent(name)}`}>
      Back to {name}
    </Link>
  </div>
);

// rememberedView is the last tab this app was on (per app, across sessions).
const rememberedView = (name) => {
  try {
    const v = localStorage.getItem("hostit.view." + name);
    return v === "overview" ? "settings" : v || "assistant";
  } catch {
    return "assistant";
  }
};

const NotFound = ({ name }) => (
  <div className="card">
    <h2>This app does not exist (or is not yours)</h2>
    <p className="empty">
      There is no app called <b>{name}</b> on your account.
    </p>
    <Link className="btn" to="/">
      Back to apps
    </Link>
  </div>
);

// "Bring your own Claude": just the ready-to-paste prompt (which already carries
// the app's URL and token). The token's own copy/regenerate controls live in the
// Actions menu now.
const PromptDialog = ({ prompt, infoUrl, token, onClose, showToast }) => {
  const copied = () => showToast && showToast("Copied to clipboard");
  // Two audiences, one dialog: paste a kick-off prompt into a chat agent, or
  // grab the app's details as individual copyable fields for your own agent's
  // context. Both panes stay mounted (stacked via CSS grid) so the modal doesn't
  // resize on tab switch.
  const [tab, setTab] = useState("agent");
  useEscape(onClose);
  // The prompt shows the token masked as "********", but the real token is what
  // gets copied -- both from the Copy button (copies the whole prompt) and from
  // a manual highlight: onCopy swaps the mask back for the token in whatever the
  // person selected, so a hand-copied prompt never carries a useless "********".
  const mask = "*".repeat(8);
  const pane = (key, hint, text, copyLabel) => {
    const shown = token ? text.split(token).join(mask) : text;
    const onCopy = (e) => {
      if (!token) return;
      const sel = window.getSelection().toString();
      if (!sel.includes(mask)) return;
      e.preventDefault();
      e.clipboardData.setData("text/plain", sel.split(mask).join(token));
    };
    return (
      <div className={"modal-tabpane" + (tab === key ? " on" : "")} role="tabpanel" aria-hidden={tab !== key}>
        <p className="hint">{hint}</p>
        <div className="term prompt-block">
          <pre onCopy={onCopy}>{shown}</pre>
          <div className="term-copy">
            <CopyButton text={text} small={false} title={copyLabel} onCopied={copied}>
              <CopyIcon />
            </CopyButton>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="card modal modal-wide modal-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <div className="modal-head">
          <h2>Use your own AI agent</h2>
        </div>
        <div className="modal-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "agent"} className={"modal-tab" + (tab === "agent" ? " on" : "")} onClick={() => setTab("agent")}>
            Start an agent
          </button>
          <button type="button" role="tab" aria-selected={tab === "details"} className={"modal-tab" + (tab === "details" ? " on" : "")} onClick={() => setTab("details")}>
            Project details
          </button>
        </div>
        <div className="modal-tabpanes">
          {pane("agent", "Paste this into Claude Code, ChatGPT or any agent: it will learn this app's API and wait for your instructions. No setup needed.", prompt, "Copy prompt")}
          {pane(
            "details",
            "Give your own agent these two. The Info URL returns everything it needs to work with this app; it authenticates with the Bearer token.",
            `Info URL: ${infoUrl}\nBearer token: ${token}`,
            "Copy details",
          )}
        </div>
      </div>
    </div>
  );
};

// How to reach the container over SSH: the ready command, an scp hint and a link
// to where the keys that make it work are managed.
const SshDialog = ({ app, hasKeys, onClose }) => {
  useEscape(onClose);
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="card modal modal-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <div className="modal-head">
          <h2>Connect via SSH</h2>
        </div>
        {hasKeys === false ? (
          <p className="hint">
            Add an SSH key under <Link to="/settings">Settings</Link> first. Keys there work on every app you own; then
            this command connects you.
          </p>
        ) : (
          <p className="hint">Shell in, or copy files with scp/rsync. The SSH keys on your profile authorize this.</p>
        )}
        {app.ssh && <Snippet text={app.ssh.command} />}
        <p className="hint" style={{ marginTop: "14px" }}>
          Manage the keys that grant access on your{" "}
          <Link to="/settings">SSH keys in Settings</Link>.
        </p>
      </div>
    </div>
  );
};

// Delete behind a type-the-name confirmation, since it takes the container, the
// files and the user with it. A modal, not a section on the page: it is reached
// deliberately from the Actions menu, never stumbled into.
const DeleteAppDialog = ({ name, onCancel, onDeleted }) => {
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const remove = async (e) => {
    e.preventDefault();
    if (busy || confirm !== name) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/apps/${encodeURIComponent(name)}`);
      onDeleted();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <form className="card modal" onSubmit={remove}>
        <h2>Delete {name}</h2>
        <p>
          This permanently removes <b>{name}</b>: its container, all its files and its Unix user.{" "}
          <strong>This cannot be undone.</strong>
        </p>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <input
          type="text"
          className="modal-input-full"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={`Type '${name}' to confirm`}
          aria-label={`Type '${name}' to confirm deletion`}
          autoFocus
        />
        <div className="btn-row">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-danger" disabled={busy || confirm !== name}>
            {busy ? "Deleting..." : "Delete app"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ConfirmDialog is a small yes/no modal that explains what an action will do.
const ConfirmDialog = ({ title, children, confirmLabel, danger, busy, onConfirm, onCancel }) => {
  useEscape(onCancel);
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onCancel}>
      <div className="card modal" onMouseDown={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <div className="confirm-body">{children}</div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={"btn " + (danger ? "btn-danger" : "btn-primary")}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// NewSnapshotDialog names and takes a manual snapshot. Reached from the snapshot
// split button's caret; the name is optional but helps the owner find it later.
const NewSnapshotDialog = ({ name, onClose, onCreated, showToast }) => {
  useEscape(onClose);
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/snapshots`, { label: label.trim() });
      showToast("Snapshot saved");
      onClose();
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <form className="card modal newapp modal-sheet" onSubmit={create} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <div className="newapp-head">
          <div className="newapp-avatar newapp-avatar-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="13" r="3.2" />
              <path d="M4 9a2 2 0 0 1 2-2h1.6l1.2-1.6h6.4L16.4 7H18a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
            </svg>
          </div>
          <div>
            <h2>New snapshot</h2>
            <p className="newapp-sub">A restorable point-in-time copy of <span className="mono">{name}</span> (files and installed software).</p>
          </div>
        </div>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <label className="newapp-label">Label <span className="newapp-optional">(optional)</span></label>
        <div className="newapp-input newapp-input-bare">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. before the big refactor"
            aria-label="Snapshot name"
            maxLength={200}
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy && <span className="newapp-spinner" aria-hidden="true" />}
            {busy ? "Saving..." : "Take snapshot"}
          </button>
        </div>
      </form>
    </div>
  );
};

// The new app's name must pass the same rule the server enforces (app.AppNamePattern).
const forkNameRe = /^[a-z]([a-z0-9-]{0,30}[a-z0-9])?$/;
const forkNameHint = "Up to 32 characters: lowercase letters, digits and dashes, starting with a letter.";

// ForkDialog duplicates the app into a new one, seeding its home from the source's
// current files -- or, when snapshotId is set, from that snapshot. Reached from the
// Fork button in the workspace top bar, or a snapshot row's Fork action.
const ForkDialog = ({ name, snapshotId, onClose, onForked }) => {
  useEscape(onClose);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = forkNameRe.test(newName.trim());

  const fork = async (e) => {
    e.preventDefault();
    const target = newName.trim();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    try {
      const created = await api.post(`/api/apps/${encodeURIComponent(name)}/fork`, {
        new_name: target,
        snapshot_id: snapshotId || undefined,
      });
      onForked(created);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const host = window.location.host;
  const sub = (newName || "").replace(/[^a-z0-9-]/g, "") || "app";
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <form className="card modal modal-sheet" onSubmit={fork} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <h2>Fork {name}</h2>
        <p className="hint">
          A new app seeded from {snapshotId ? "this snapshot" : "a copy of the current files"} -- its own subdomain, user and container. The two run independently from here on.
        </p>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <div className="fork-flow">
          <div className="fork-node">
            <span className="fork-avatar">{name.slice(0, 2)}</span>
            <div className="fork-nm">{name}</div>
            <div className="fork-sub">{snapshotId ? "snapshot" : "current files"}</div>
          </div>
          <div className="fork-arrow" aria-hidden="true">&rarr;</div>
          <div className="fork-node new">
            <span className="fork-avatar">{newName.trim() ? sub.slice(0, 2) : "?"}</span>
            <div className="fork-nm">{newName.trim() ? sub : "new app"}</div>
            <div className="fork-sub">new app</div>
          </div>
        </div>
        <div className="newapp-input">
          <span className="newapp-dollar mono">$</span>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="new app name"
            aria-label="New app name"
            autoFocus
            spellCheck="false"
            autoComplete="off"
            maxLength={32}
          />
        </div>
        <p className="hint fork-hint">{forkNameHint}</p>
        <div className="newapp-willbe">
          <div className="row">
            <span className="ico">{"\u{1F310}"}</span>
            <span className="lab">URL</span>
            <span className="val">https://<b>{sub}</b>.{host}</span>
          </div>
          <div className="row">
            <span className="ico">{"\u{2328}\u{FE0F}"}</span>
            <span className="lab">SSH</span>
            <span className="val"><b>{sub}</b>@{host}</span>
          </div>
        </div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy ? "Forking..." : "Fork app"}
          </button>
        </div>
      </form>
    </div>
  );
};

// RenameDialog changes an app's name in place. The app keeps running and nothing
// moves: its home, container, snapshots and custom domains all follow it. Only the
// built-in <name>.<base> subdomain and the SSH login change. Reached from the
// SshMenuIcon is the ">_" terminal glyph for the SSH connect button/menu item.
const SshMenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6l5 5-5 5" />
    <path d="M12 17h8" />
  </svg>
);

// Rename icon next to "App name" in the Settings view.
// The resources dialog's menus: RAM and disk in MB, CPU in cores.
const MEMORY_PRESETS = [128, 256, 512, 1024, 2048, 4096];
const DISK_PRESETS = [256, 512, 1024, 2048, 4096, 8192, 16384];
const CPU_PRESETS = [0.1, 0.25, 0.5, 1, 2, 4];

// ResourceRow is one line of the resources dialog: a label and a preset
// dropdown, in the same visual rhythm as the Resources card's metric rows.
// The empty value is "inherit"; a current override that is not a preset is
// kept as its own option rather than silently snapping to one.
const ResourceRow = ({ label, value, onChange, presets, unit, inherit, disabled, max = Infinity, min, error, custom, onCustom }) => {
  const values = presets.map(String);
  // In "Custom..." a size is typed with its own unit; CPU is typed in cores
  const sized = unit === "MB";
  const [text, setText] = useState(() => (sized ? sizeFromMB(value).text : String(value)));
  const [sizeUnit, setSizeUnit] = useState(() => (sized ? sizeFromMB(value).unit : unit));
  const fmt = (v) => {
    if (unit === "cores") return cores(Number(v));
    return Number(v) >= 1024 ? `${Number(v) / 1024} GB` : `${v} ${unit}`;
  };
  const pick = (v) => {
    if (v === "custom") {
      const init = sized ? sizeFromMB(value) : { text: String(value), unit };
      setText(init.text);
      setSizeUnit(init.unit);
      onCustom(true);
      return;
    }
    onCustom(false);
    onChange(v);
  };
  const type = (t, u) => {
    setText(t);
    setSizeUnit(u);
    onChange(sized ? sizeToMB(t, u) : t.trim());
  };
  return (
    <div className="res-row">
      <span className="res-label">{label}</span>
      <div className="res-control">
        <select className="settings-input res-select" value={custom ? "custom" : String(value)} onChange={(e) => pick(e.target.value)} disabled={disabled} aria-label={label}>
          <option value="">{inherit}</option>
          {values.map((v) => (
            // Anything past the pool's headroom is visible but not pickable: the
            // menu teaches the budget instead of letting the server 403 it.
            <option key={v} value={v} disabled={Number(v) > max}>
              {fmt(v)}{Number(v) > max ? " (over your pool)" : ""}
            </option>
          ))}
          <option value="custom">Custom...</option>
        </select>
        {custom && (
          <span className="res-custom">
            <input
              type="text"
              className="settings-input res-custom-input"
              value={text}
              onChange={(e) => type(e.target.value, sizeUnit)}
              inputMode="decimal"
              disabled={disabled}
              aria-label={`Custom ${label}`}
              aria-invalid={!!error}
            />
            {sized ? (
              <select className="settings-input res-unit-select" value={sizeUnit} onChange={(e) => type(text, e.target.value)} disabled={disabled} aria-label={`${label} unit`}>
                <option value="MB">MB</option>
                <option value="GB">GB</option>
              </select>
            ) : (
              <span className="res-unit">{unit}</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

// SnapshotIntervalSelect picks the automatic snapshot cadence from presets;
// a custom hostit.yml value survives as its own option, and 0 turns automatic
// snapshots off (pre-deploy snapshots still happen).
const SnapshotIntervalSelect = ({ value, defaultLabel, onChange }) => {
  const presets = ["1h", "3h", "6h", "12h", "24h"];
  const options = value !== "" && value !== "0" && !presets.includes(value) ? [value, ...presets] : presets;
  return (
    <select className="settings-input res-select" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Snapshot interval">
      <option value="">Default ({defaultLabel})</option>
      {options.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
      <option value="0">Off (pre-deploy only)</option>
    </select>
  );
};

// ResourcesDialog edits the app's resource allocation: RAM and disk within
// the owner's pool (the server enforces the pool; the dialog shows the
// budget), CPU admin-set. Empty fields inherit the account defaults.
// Who can see the app, in one place: the setting itself and the people let in.
// They belong together because "private" and "who else" are one decision made
// in one sitting -- split across two sections is how an app ends up private
// with nobody able to open it.
//
// Nothing here touches the server until Save. The viewer list is edited as a
// draft, so adding two people and changing your mind costs nothing, and the
// whole dialog commits or does not. The viewer section stays mounted and
// merely disables itself when the app is public, so the dialog never changes
// height as you switch between the two.
const VisibilityDialog = ({ app, isPrivate, viewers, collabs, allowListed, onSave, onClose }) => {
  useEscape(onClose);
  // The draft is one of the visibility states, not a private/public boolean:
  // "restricted" is what the people list is FOR, so picking it is what reveals
  // the list; "listed" is public plus the Explore gallery. It starts on whatever
  // the app is now.
  const [draft, setDraft] = useState(() => visibilityOf(isPrivate, viewers === null ? app.viewer_count : viewers.length, app.listed));
  const [added, setAdded] = useState([]);
  const [dropped, setDropped] = useState([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // The list is edited as a diff against whatever has loaded, rather than as a
  // snapshot taken when the dialog opened: the viewer list arrives a moment
  // after the page, and a snapshot taken in that window would be empty and
  // would make Save revoke everybody.
  const people = [...(viewers || []).filter((v) => !dropped.includes(v.id)), ...added];
  // "Private" means private with NOBODY named, so choosing it commits an empty
  // list (which revokes any viewers the app had). "Restricted" commits the
  // edited list; "public" flips the flag and leaves the list untouched.
  const draftPrivate = draft === "private" || draft === "restricted";
  const draftListed = draft === "listed";
  const effectivePeople = draft === "private" ? [] : people;
  const changes = visibilityChanges(viewers, effectivePeople, isPrivate, draftPrivate, !!app.listed, draftListed);

  const add = (e) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address) return;
    if (people.some((p) => p.email === address)) {
      setError(`${address} is already on the list.`);
      return;
    }
    setError("");
    // No id yet: this person exists only in the draft until Save resolves them
    // against a real account, which is also where "they never signed up" surfaces.
    setAdded([...added, { email: address }]);
    setEmail("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    const failure = await onSave(changes);
    setSaving(false);
    if (failure) {
      setError(failure);
      return;
    }
    onClose();
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="card modal modal-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <h2>Who can see {app.name}?</h2>
        <p className="hint vis-intro">
          Public is open to anyone with the link. Private asks visitors to sign in and lets through only
          you, your collaborators, and admins. Restricted is private plus the specific people you name.
          {allowListed ? " Listed is public and also on the instance's Explore gallery." : ""}
        </p>
        <VisibilityChoice value={draft} onChange={setDraft} disabled={saving} allowListed={allowListed} />

        {/* Always shown -- disabled, not hidden -- when the app is not Restricted,
            so the modal does not jump and it is clear what Restricted adds. */}
        <fieldset className="vis-people" disabled={draft !== "restricted" || saving}>
          <label className="newapp-label vis-people-lab">People with access</label>
          <p className="hint vis-people-hint">
            They can open the app and nothing else -- no files, no terminal, no deploys. Someone with no
            account yet is invited and gets access the first time they sign in. Your{" "}
            {collabs && collabs.length > 0 ? `${collabs.length} collaborator${collabs.length === 1 ? "" : "s"}` : "collaborators"}{" "}
            can already see it without being listed here.
          </p>
          <form className="domain-add" onSubmit={add}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="someone@example.com" aria-label="Give access to" />
            <button type="submit" className="btn btn-primary btn-small" disabled={!email.trim()}>Add</button>
          </form>
          {viewers === null ? (
            <p className="hint vis-people-empty">Loading...</p>
          ) : people.length === 0 ? (
            <p className="hint vis-people-empty">Nobody added yet.</p>
          ) : (
            <div className="domain-list vis-people-list">
              {people.map((u) => (
                <div className="domain-row" key={u.email}>
                  <div className="domain-head">
                    <span className="domain-name">{u.email}</span>
                    {u.pending && <span className="viewer-pending" title="Gets access the first time they sign in">Pending sign-in</span>}
                    {u.name && u.name !== u.email && <span className="collab-name">{u.name}</span>}
                    <span className="domain-actions">
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => (u.id ? setDropped([...dropped, u.id]) : setAdded(added.filter((a) => a.email !== u.email)))}
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </fieldset>

        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !changes.changed}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ResourcesDialog = ({ app, isAdmin, account, showToast, onClose, onSaved }) => {
  useEscape(onClose);
  const [cfg, setCfg] = useState(() => limitInputs(app.limit_overrides));
  // Which rows are in "Custom...": a stored override that is no preset opens there
  const [customs, setCustoms] = useState(() => {
    const c = limitInputs(app.limit_overrides);
    const off = (v, presets) => v !== "" && !presets.map(String).includes(String(v));
    return { memory: off(c.memory, MEMORY_PRESETS), disk: off(c.disk, DISK_PRESETS), cpu: off(c.cpu, CPU_PRESETS) };
  });
  const setCustom = (key) => (on) => setCustoms((c) => ({ ...c, [key]: on }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pool = (account && account.limits) || {};
  const allocated = (account && account.usage) || {};
  // What THIS app may grow to: the pool minus everyone else's allocation.
  // Only meaningful when the viewer's own pool is the one that binds (an
  // admin may be editing someone else's app, whose owner's pool we don't
  // have here -- the server still enforces it).
  // The server holds admins to the pool too, so their OWN app is capped by it
  const ownPool = !isAdmin || app.is_owner;
  const headroom = (poolMB, allocatedMB, currentMB) =>
    ownPool && poolMB > 0 ? poolMB - (allocatedMB || 0) + (currentMB || 0) : Infinity;
  const maxMemory = headroom(pool.memory_pool_mb, allocated.pool_memory_mb, app.memory_limit_mb);
  const maxDisk = headroom(pool.disk_pool_mb, allocated.pool_disk_mb, app.disk_limit_mb);
  // A typed-in value is checked here; the floors are the server's (server_handler_limits.go)
  const errors = {
    memory: customLimitError(cfg.memory, { min: 16, max: maxMemory, unit: "MB" }),
    disk: customLimitError(cfg.disk, { min: 256, max: maxDisk, unit: "MB" }),
    cpu: isAdmin ? customLimitError(cfg.cpu, { min: 0.1, max: Infinity, unit: "cores" }) : "",
  };
  // A custom row left empty has nothing to save (the menu's first entry is the default)
  // What is wrong, named by field, for the top of the dialog
  const problems = [["RAM", "memory"], ["Disk", "disk"], ["CPU", "cpu"]].filter(([, k]) => customs[k] && errors[k]).map(([label, k]) => `${label}: ${errors[k]}`);
  const invalid = Object.values(errors).some(Boolean) || Object.keys(customs).some((k) => customs[k] && String(cfg[k]).trim() === "");

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const body = limitsPatchBody(cfg, app.limit_overrides);
      if (!isAdmin) body.cpu_milli = 0; // CPU is admin-set; 0 leaves it alone
      await api.patch(`/api/apps/${encodeURIComponent(app.name)}/limits`, body);
      showToast("Resources saved; RAM and CPU apply at the next reboot or deploy");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <form className="card modal modal-sheet" onMouseDown={(e) => e.stopPropagation()} onSubmit={save}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <h2>Resources for {app.name}</h2>
        <p className="hint" style={{ marginBottom: "5px" }}>
          Empty fields inherit your account defaults. Disk applies immediately; RAM and CPU at the
          next reboot or deploy.
          {pool.memory_pool_mb > 0 && (
            <>
              {" "}Across all your apps you may allocate <b>{pairMB(pool.memory_pool_mb, 0)}</b> RAM
              and <b>{pairMB(pool.disk_pool_mb, 0)}</b> disk. You have{" "}
              <b>{pairMB(Math.max(0, pool.memory_pool_mb - (allocated.pool_memory_mb || 0)), 0)}</b> RAM and{" "}
              <b>{pairMB(Math.max(0, pool.disk_pool_mb - (allocated.pool_disk_mb || 0)), 0)}</b> disk available.
            </>
          )}
        </p>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <div className="res-rows">
          <ResourceRow
            label="RAM"
            value={cfg.memory}
            onChange={(v) => setCfg({ ...cfg, memory: v })}
            presets={MEMORY_PRESETS}
            custom={customs.memory}
            onCustom={setCustom("memory")}
            unit="MB"
            inherit={`Default (${pool.memory_mb || 0} MB)`}
            disabled={busy}
            max={maxMemory}
            min={16}
            error={errors.memory}
          />
          <ResourceRow
            label="Disk"
            value={cfg.disk}
            onChange={(v) => setCfg({ ...cfg, disk: v })}
            presets={DISK_PRESETS}
            custom={customs.disk}
            onCustom={setCustom("disk")}
            unit="MB"
            inherit={`Default (${pool.disk_mb || 0} MB)`}
            disabled={busy}
            max={maxDisk}
            min={256}
            error={errors.disk}
          />
          {isAdmin ? (
            <ResourceRow
              label="CPU"
              value={cfg.cpu}
              onChange={(v) => setCfg({ ...cfg, cpu: v })}
              presets={CPU_PRESETS}
              custom={customs.cpu}
              onCustom={setCustom("cpu")}
              unit="cores"
              inherit="Uncapped"
              disabled={busy}
              min={0.1}
              error={errors.cpu}
            />
          ) : (
            app.cpu_milli > 0 && (
              <div className="res-row">
                <span className="res-label">CPU</span>
                <span className="res-fixed">{cores(app.cpu_milli / 1000)} (admin-set)</span>
              </div>
            )
          )}
        </div>
        <div className="btn-row res-btn-row">
          {/* What is wrong, named by field, on the buttons' line */}
          {problems.length > 0 && (
            <div className="res-problems" role="alert">
              {problems.map((p) => (
                <div key={p}>{p}</div>
              ))}
            </div>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || invalid}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

// TransferDialog hands the app to another user. The current owner stays on as
// a collaborator, so transferring never locks anyone out of their own work.
const TransferDialog = ({ name, onClose, onTransferred }) => {
  useEscape(onClose);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /.+@.+/.test(email.trim());

  const transfer = async (e) => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/transfer`, { email: email.trim().toLowerCase() });
      onTransferred();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <form className="card modal modal-sheet" onSubmit={transfer} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <h2>Transfer {name}</h2>
        <p className="hint" style={{ marginBottom: "5px" }}>
          The new owner must be an existing, approved user. The app keeps running and nothing else changes; you stay on as a <b>collaborator</b> with full working access, but only the new owner can delete or rename it, or manage collaborators.
        </p>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <div className="newapp-input newapp-input-bare">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new-owner@example.com"
            aria-label="New owner email"
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy && <span className="newapp-spinner" aria-hidden="true" />}
            {busy ? "Transferring..." : "Transfer ownership"}
          </button>
        </div>
      </form>
    </div>
  );
};

const RenameDialog = ({ name, onClose, onRenamed }) => {
  useEscape(onClose);
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const target = newName.trim();
  const valid = forkNameRe.test(target) && target !== name;

  const rename = async (e) => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    try {
      const updated = await api.post(`/api/apps/${encodeURIComponent(name)}/rename`, { new_name: target });
      onRenamed(updated);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const host = window.location.host;
  const sub = target.replace(/[^a-z0-9-]/g, "") || "app";
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <form className="card modal modal-sheet" onSubmit={rename} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <h2>Rename {name}</h2>
        <p className="hint" style={{ marginBottom: "5px" }}>
          The app keeps running, and its files, container and custom domains all follow it. Only the <b>{name}.{host}</b> subdomain and the SSH login change. Links to the old subdomain stop working.
        </p>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <div className="fork-flow">
          <div className="fork-node">
            <span className="fork-avatar">{name.slice(0, 2)}</span>
            <div className="fork-nm">{name}</div>
            <div className="fork-sub">now</div>
          </div>
          <div className="fork-arrow" aria-hidden="true">&rarr;</div>
          <div className="fork-node new">
            <span className="fork-avatar">{target ? sub.slice(0, 2) : "?"}</span>
            <div className="fork-nm">{target ? sub : "new name"}</div>
            <div className="fork-sub">same app</div>
          </div>
        </div>
        <div className="newapp-input">
          <span className="newapp-dollar mono">$</span>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="new name"
            aria-label="New app name"
            autoFocus
            spellCheck="false"
            autoComplete="off"
            maxLength={32}
          />
        </div>
        <p className="hint fork-hint">{forkNameHint}</p>
        <div className="newapp-willbe">
          <div className="row">
            <span className="ico">{"\u{1F310}"}</span>
            <span className="lab">URL</span>
            <span className="val">https://<b>{sub}</b>.{host}</span>
          </div>
          <div className="row">
            <span className="ico">{"\u{2328}\u{FE0F}"}</span>
            <span className="lab">SSH</span>
            <span className="val"><b>{sub}</b>@{host}</span>
          </div>
        </div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy ? "Renaming..." : "Rename app"}
          </button>
        </div>
      </form>
    </div>
  );
};

// The Settings view: the app's identity + details (address, access, resources)
// with the former Settings dialog folded in -- editable description, API token,
// and custom domains -- all inline in one tab.
const AppSettings = ({ app, isAdmin, account, showToast, onCopyToken, onRegenerateToken, hasToken, onSaved, onConfigureKeys, showVisibility, setShowVisibility }) => {
  const name = app.name;
  const navigate = useNavigate();
  const [desc, setDesc] = useState(app.description || "");
  const [savingDesc, setSavingDesc] = useState(false);
  const snap = app.snapshot || {};
  // Go prints a duration as "3h0m0s"; a placeholder should read "3h".
  const prettyDuration = (d) => (d || "").replace(/0m0s$/, "").replace(/(\dh)0m$/, "$1").replace(/^0s$/, "0");
  const defaultInterval = prettyDuration(snap.default_interval) || "3h";
  const [snapCfg, setSnapCfg] = useState({ interval: snap.interval || "", pre: snap.pre || "", post: snap.post || "" });
  const [savingSnap, setSavingSnap] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [domains, setDomains] = useState(null);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [collabs, setCollabs] = useState(null);
  const [viewers, setViewers] = useState(null);
  // showVisibility is lifted to AppDetail so the always-visible title-bar badge
  // can open this dialog too, not just the overview row here.
  const [collabInput, setCollabInput] = useState("");
  const [showTransfer, setShowTransfer] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const isOwner = !!app.is_owner;

  const pct = (u, l) => (l ? Math.min(100, Math.round((u / l) * 100)) : 0);
  const mb = (u, l) => (l ? `${u} / ${l} MB` : `${u} MB`);
  // The app's own URL plus any verified custom domain, all as clickable links.
  const urls = [app.url, ...(domains || []).filter((d) => d.status === "active").map((d) => "https://" + d.domain)];
  // "user@host", from the ready-made ssh command without its leading "ssh ".
  const sshTarget = (app.ssh && app.ssh.command ? app.ssh.command : "").replace(/^ssh\s+/, "");

  const saveDescription = async () => {
    if (savingDesc) return;
    setSavingDesc(true);
    setError("");
    try {
      await api.put(`/api/apps/${encodeURIComponent(name)}/description`, { description: desc.trim() });
      showToast("Description saved");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDesc(false);
    }
  };

  const snapDirty =
    snapCfg.interval !== (snap.interval || "") ||
    snapCfg.pre !== (snap.pre || "") ||
    snapCfg.post !== (snap.post || "");

  const saveSnapshots = async () => {
    if (savingSnap) return;
    setSavingSnap(true);
    setError("");
    try {
      await api.put(`/api/apps/${encodeURIComponent(name)}/snapshot-config`, snapCfg);
      showToast("Snapshot settings saved");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSnap(false);
    }
  };

  // Commits everything the visibility dialog collected, in one go. Returns a
  // message on failure so the dialog can show it in place and stay open with
  // the draft intact -- the most likely failure is naming somebody who has
  // never signed in, and losing the rest of the edit over that would be rude.
  const saveVisibility = async ({ isPrivate, add, remove, listed }) => {
    const path = `/api/apps/${encodeURIComponent(name)}`;
    try {
      if (isPrivate !== !!app.private || !!listed !== !!app.listed) {
        await api.put(`${path}/visibility`, { private: isPrivate, listed: !!listed });
      }
      for (const email of add) {
        await api.post(`${path}/viewers`, { email });
      }
      for (const id of remove) {
        await api.del(`${path}/viewers/${encodeURIComponent(id)}`);
      }
    } catch (err) {
      await load(); // whatever did land is now on screen
      if (onSaved) onSaved();
      return err.message;
    }
    showToast("Visibility saved");
    await load();
    if (onSaved) onSaved();
    return "";
  };

  const onRenamed = (updated) => {
    // Close the dialog explicitly: navigating to the new name keeps this component
    // mounted (same route, changed param), so showRename would otherwise stay open.
    setShowRename(false);
    showToast("App renamed");
    navigate(`/app/${encodeURIComponent(updated.name)}`, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const list = await api.get(`/api/apps/${encodeURIComponent(name)}/domains`);
      setDomains(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message);
      setDomains([]);
    }
    try {
      const people = await api.get(`/api/apps/${encodeURIComponent(name)}/collaborators`);
      setCollabs(Array.isArray(people) ? people : []);
    } catch {
      setCollabs([]); // the list is a nicety; the section still renders
    }
    try {
      const guests = await api.get(`/api/apps/${encodeURIComponent(name)}/viewers`);
      setViewers(Array.isArray(guests) ? guests : []);
    } catch {
      setViewers([]);
    }
  }, [name]);
  useEffect(() => {
    load();
  }, [load]);
  // Prefer the list this page just loaded, so the badge updates the moment
  // somebody is added, rather than on the next poll of the app itself.
  const visibility = visibilityOf(!!app.private, viewers === null ? app.viewer_count : viewers.length, !!app.listed);
  const anyPending = domains && domains.some((d) => d.status !== "active");
  useEffect(() => {
    if (!anyPending) return undefined;
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [anyPending, load]);

  const addDomain = async (e) => {
    e.preventDefault();
    const domain = input.trim().toLowerCase();
    if (busy || !domain) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/domains`, { domain });
      setInput("");
      showToast("Domain added; create the DNS records");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const verifyDomain = async (domain) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/domains/${encodeURIComponent(domain)}/verify`, {});
      showToast("Checking DNS...");
      setTimeout(load, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  // Adding a collaborator is a sharing decision with teeth: they get the app's
  // files and secrets, and can read the credential of every connection granted
  // to it, as the app would. So the request goes through a warning that names
  // those connections first; the POST itself happens on confirm.
  const [collabConfirm, setCollabConfirm] = useState(null); // {email, granted}
  const [collabRemove, setCollabRemove] = useState(null); // the collaborator being removed
  const [rotateOnRemove, setRotateOnRemove] = useState(true);
  const collabNoun = app.kind === "chat" ? "chat" : "app";
  const addCollaborator = async (e) => {
    e.preventDefault();
    const email = collabInput.trim().toLowerCase();
    if (busy || !email) return;
    let granted = [];
    try {
      granted = (await api.get(`/api/apps/${encodeURIComponent(name)}/connections`)).granted || [];
    } catch {
      // The warning still shows; it just cannot name the connections.
    }
    setCollabConfirm({ email, granted });
  };
  const confirmCollaborator = async () => {
    if (busy || !collabConfirm) return;
    const { email } = collabConfirm;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/collaborators`, { email });
      setCollabInput("");
      setCollabConfirm(null);
      showToast("Collaborator added");
      await load();
    } catch (err) {
      setCollabConfirm(null);
      showToast(err.message); // e.g. "no active user ..." -- a toast, not a banner
    } finally {
      setBusy(false);
    }
  };
  const removeCollaborator = async (id, rotate) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/apps/${encodeURIComponent(name)}/collaborators/${encodeURIComponent(id)}${rotate ? "?rotate_token=true" : ""}`);
      setCollabRemove(null);
      showToast(rotate ? "Collaborator removed; app token regenerated" : "Collaborator removed");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const removeDomain = async (domain) => {
    if (busy) return;
    setConfirmId(null);
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/apps/${encodeURIComponent(name)}/domains/${encodeURIComponent(domain)}`);
      showToast("Domain removed");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ov">
      <div className="ov-hero ov-hero-lite">
        <div className="ov-id">
          <div className="ov-nm">Settings</div>
          <div className="ov-desc">
            What {name} is called and where it answers, who may work on it, how much it may use, and
            the token an outside agent drives it with.
          </div>
        </div>
      </div>
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      <div className="ov-cols">
        <div>
          <h3>App details</h3>
          <div className="ov-line ov-line-top">
            <span className="ov-k">Name</span>
            <span className="ov-v">{name}</span>
            <CopyMini text={name} label="Copy app name" />
            <button
              type="button"
              className="copy-mini"
              onClick={() => isOwner && setShowRename(true)}
              disabled={!isOwner}
              title={isOwner ? "Rename app" : "Only the owner can rename the app"}
              aria-label="Rename app"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11.5 2.5l2 2L6 12l-2.6.6L4 10z" />
                <path d="M10.5 3.5l2 2" />
              </svg>
            </button>
          </div>
          <div className="ov-line">
            <span className="ov-k">{urls.length > 1 ? "URLs" : "URL"}</span>
            <span className="ov-urls">
              {urls.map((u) => (
                <span className="ov-url-row" key={u}>
                  <a href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, "")}</a>
                  <CopyMini text={u} label="Copy URL" />
                </span>
              ))}
            </span>
          </div>
          <div className="ov-line">
            <span className="ov-k">SSH</span>
            <span className="ov-v">{sshTarget}</span>
            <CopyMini text={sshTarget} label="Copy SSH command" />
            <button type="button" className="copy-mini" onClick={onConfigureKeys} title="Configure SSH keys" aria-label="Configure SSH keys">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="5.5" cy="10.5" r="2.5" />
                <path d="M7.3 8.7 13 3M11 5l1.5 1.5M9.5 6.5 11 8" />
              </svg>
            </button>
          </div>
          <div className="ov-line">
            <span className="ov-k">Token</span>
            <span className="ov-v ov-mask">{"•".repeat(12)}</span>
            <button type="button" className="copy-mini" onClick={onCopyToken} disabled={!hasToken} title="Copy token" aria-label="Copy token">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="5.5" y="5.5" width="8" height="8.5" rx="1.3" />
                <path d="M3.5 10.5V3.2a1 1 0 0 1 1-1H10" />
              </svg>
            </button>
            {/* Collaborators can copy the token; rotating breaks the owner's agents, so it is theirs */}
            {(app.is_owner || isAdmin) && (
              <button type="button" className="copy-mini" onClick={onRegenerateToken} title="Regenerate token" aria-label="Regenerate token">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M13 8a5 5 0 1 1-1.5-3.5" />
                  <path d="M13 3v2.2h-2.2" />
                </svg>
              </button>
            )}
          </div>
          <div className="ov-line">
            <span className="ov-k">Visibility</span>
            <span className="ov-v">
              {isOwner ? (
                <button
                  type="button"
                  className="vis-badge-btn"
                  onClick={() => setShowVisibility(true)}
                  title="Change who can see this app"
                >
                  <VisibilityBadge state={visibility} />
                </button>
              ) : (
                <VisibilityBadge state={visibility} />
              )}
            </span>
            <button
              type="button"
              className="copy-mini"
              onClick={() => isOwner && setShowVisibility(true)}
              disabled={!isOwner}
              title={isOwner ? "Change who can see this app" : "Only the owner can change visibility"}
              aria-label="Change visibility"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11.3 2.2l2.5 2.5L6 12.5l-3.2.7.7-3.2z" />
              </svg>
            </button>
          </div>
          {app.owner_email && (
            <div className="ov-line">
              <span className="ov-k">Owner</span>
              <span className="ov-v" title={app.owner_email}>{app.owner_name || app.owner_email}</span>
              <button
                type="button"
                className="copy-mini"
                onClick={() => isOwner && setShowTransfer(true)}
                disabled={!isOwner}
                title={isOwner ? "Transfer ownership" : "Only the owner can transfer ownership"}
                aria-label="Transfer ownership"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2.5 5.5h9M9 3l2.5 2.5L9 8" />
                  <path d="M13.5 10.5h-9M7 8l-2.5 2.5L7 13" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div>
          <h3 className="ov-title-row">
            Resources
            {isOwner && (
              <button
                type="button"
                className="copy-mini"
                onClick={() => setShowResources(true)}
                title="Edit resource limits"
                aria-label="Edit resource limits"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11.3 2.2l2.5 2.5L6 12.5l-3.2.7.7-3.2z" />
                </svg>
              </button>
            )}
          </h3>
          <div className="ov-metric"><div className="ov-mt"><span>CPU</span><span className="mono">{app.cpu_percent || 0}%{app.cpu_milli ? ` (cap ${cores(app.cpu_milli / 1000)})` : ""}</span></div><div className="ov-bar"><i style={{ width: `${app.cpu_percent || 0}%` }} /></div></div>
          <div className="ov-metric"><div className="ov-mt"><span>RAM</span><span className={"mono usage-" + usageLevel(app.memory_mb, app.memory_limit_mb)}>{pairMB(app.memory_mb, app.memory_limit_mb)}</span></div><div className="ov-bar"><i className={usageLevel(app.memory_mb, app.memory_limit_mb)} style={{ width: `${pct(app.memory_mb, app.memory_limit_mb)}%` }} /></div></div>
          <div className="ov-metric"><div className="ov-mt"><span>Disk</span><span className={"mono usage-" + usageLevel(app.disk_mb, app.disk_limit_mb)}>{pairMB(app.disk_mb, app.disk_limit_mb)}</span></div><div className="ov-bar"><i className={usageLevel(app.disk_mb, app.disk_limit_mb)} style={{ width: `${pct(app.disk_mb, app.disk_limit_mb)}%` }} /></div></div>
        </div>
      </div>

      <section className="ov-section">
        <h3>Description</h3>
        <p className="hint">A one-line summary of the app, shown on the dashboard and where the assistant starts from. Saved to <span className="mono">hostit.yml</span>.</p>
        <textarea className="settings-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} maxLength={280} placeholder="What this app is..." />
        <div className="btn-row" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="btn btn-small btn-primary" onClick={saveDescription} disabled={savingDesc || desc.trim() === (app.description || "").trim()}>
            {savingDesc ? "Saving..." : "Save description"}
          </button>
        </div>
      </section>

      <section className="ov-section">
        <h3>Snapshots</h3>
        <p className="hint">
          hostit snapshots this app automatically, and before every deploy. Leave the interval blank for the
          default ({defaultInterval}), or set <span className="mono">0</span> to turn automatic
          snapshots off -- pre-deploy snapshots still happen. Saved to <span className="mono">hostit.yml</span>.
        </p>
        <div className="res-rows">
          <div className="res-row">
            <span className="res-label">Every</span>
            <SnapshotIntervalSelect
              value={snapCfg.interval}
              defaultLabel={defaultInterval}
              onChange={(v) => setSnapCfg({ ...snapCfg, interval: v })}
            />
          </div>
        </div>
        <details className="snap-hooks" open={!!(snapCfg.pre || snapCfg.post)}>
          <summary>Pre/post snapshot commands</summary>
          <p className="hint">
            If the app has a database, these run in the container around each snapshot, so what is
            captured is consistent. A failing <span className="mono">pre</span> command cancels the
            snapshot rather than saving a torn state. Paste whole scripts; they run with{" "}
            <span className="mono">sh -c</span>.
          </p>
          <label className="settings-field">
            <span>Before (pre)</span>
            <textarea
              className="settings-desc mono snap-script"
              rows={2}
              value={snapCfg.pre}
              onChange={(e) => setSnapCfg({ ...snapCfg, pre: e.target.value })}
              placeholder={'sqlite3 data/app.db ".backup data/app.snap.db"'}
            />
          </label>
          <label className="settings-field">
            <span>After (post)</span>
            <textarea
              className="settings-desc mono snap-script"
              rows={2}
              value={snapCfg.post}
              onChange={(e) => setSnapCfg({ ...snapCfg, post: e.target.value })}
              placeholder="rm -f data/app.snap.db"
            />
          </label>
        </details>
        <div className="btn-row" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="btn btn-small btn-primary" onClick={saveSnapshots} disabled={savingSnap || !snapDirty}>
            {savingSnap ? "Saving..." : "Save snapshot settings"}
          </button>
        </div>
      </section>


      <section className="ov-section">
        <h3>Collaborators</h3>
        <p className="hint">
          {!isOwner && app.owner_email && <>This app is owned by <b>{app.owner_name || app.owner_email}</b>. </>}
          Collaborators can deploy, edit files, use the terminal and assistant, and SSH in with their own keys.
          {isOwner ? " Only you (the owner) can delete or rename the app, or change this list." : " Only the owner can change this list."}
        </p>
        {isOwner && (
          <form className="domain-add" onSubmit={addCollaborator}>
            <input type="email" value={collabInput} onChange={(e) => setCollabInput(e.target.value)} placeholder="teammate@example.com" aria-label="Collaborator email" />
            <button type="submit" className="btn btn-primary btn-small" disabled={busy || !collabInput.trim()}>Add collaborator</button>
          </form>
        )}
        {collabRemove && (
          <ConfirmDialog
            title={`Remove ${collabRemove.email}?`}
            confirmLabel="Remove"
            busy={busy}
            danger
            onConfirm={() => removeCollaborator(collabRemove.id, rotateOnRemove)}
            onCancel={() => setCollabRemove(null)}
          >
            <p>They lose access to this {collabNoun}: its files, terminal, logs and connections.</p>
            <div className="collab-rotate">
              <button type="button" role="switch" aria-checked={rotateOnRemove} aria-labelledby="collab-rotate-label" className={"conn-switch" + (rotateOnRemove ? " on" : "")} onClick={() => setRotateOnRemove(!rotateOnRemove)} disabled={busy}>
                <span className="conn-switch-knob" />
              </button>
              <div>
                <div id="collab-rotate-label" className="collab-rotate-label" onClick={() => setRotateOnRemove(!rotateOnRemove)}>Regenerate the app token</div>
                <div className="hint">They may have copied it. Agents and scripts using the current token stop working until you give them the new one.</div>
              </div>
            </div>
          </ConfirmDialog>
        )}
        {collabConfirm && (
          <ConfirmDialog
            title={<><span className="collab-warn-icon" aria-hidden="true"><WarnIcon /></span> Share this {collabNoun} with {collabConfirm.email}?</>}
            confirmLabel="I understand, add them"
            busy={busy}
            onConfirm={confirmCollaborator}
            danger
            onCancel={() => setCollabConfirm(null)}
          >
            <>
              <p className="collab-warn-lead">
                <WarnIcon /> <b>You are about to share your credentials.</b> A collaborator has full working
                access: every file in this {collabNoun}, its terminal, its logs, and any
                secret stored in it (API keys in files, environment variables, tokens the assistant
                was given), and they can copy the app's API token.
              </p>
              {collabConfirm.granted.length > 0 ? (
                <>
                  <p>
                    They can also read the live credential of every connection granted to it, exactly
                    as the {collabNoun} does, and use it as you:
                  </p>
                  <ul className="collab-warn-list">
                    {collabConfirm.granted.map((c) => (
                      <li key={c.slug}>
                        <ConnIcon url={c.icon_url} />
                        <b>{c.label || c.slug}</b>
                        <span className="conn-provider">{c.provider_label}</span>
                      </li>
                    ))}
                  </ul>
                  <p>
                    Revoking a grant later cuts them off from that connection, but anything they have
                    already copied stays copied.
                  </p>
                </>
              ) : (
                <p>No connections are granted to it right now; any you grant later are shared the same way.</p>
              )}
            </>
          </ConfirmDialog>
        )}
        {collabs === null ? (
          <p className="hint">Loading...</p>
        ) : collabs.length === 0 ? null : (
          <div className="domain-list">
            {collabs.map((u) => (
              <div className="domain-row" key={u.id}>
                <div className="domain-head">
                  <span className="domain-name">{u.email}</span>
                  {u.name && u.name !== u.email && <span className="collab-name">{u.name}</span>}
                  {isOwner && (
                    <span className="domain-actions">
                      <button type="button" className="btn btn-small" onClick={() => { setRotateOnRemove(true); setCollabRemove(u); }} disabled={busy}>Remove</button>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="ov-section">
        <h3>Custom domains</h3>
        <p className="hint">Serve <span className="mono">{name}</span> on your own hostname. Add it, then create the two DNS records shown; the certificate issues automatically.</p>
        <form className="domain-add" onSubmit={addDomain}>
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="blog.example.com" aria-label="Custom domain" />
          <button type="submit" className="btn btn-primary btn-small" disabled={busy || !input.trim()}>Add domain</button>
        </form>
        {domains === null ? (
          <p className="hint">Loading...</p>
        ) : domains.length === 0 ? null : (
          <div className="domain-list">
            {domains.map((d) => (
              <div className="domain-row" key={d.domain}>
                <div className="domain-head">
                  <span className="domain-name">{d.domain}</span>
                  <span className={"domain-status domain-" + d.status}>{d.status}</span>
                  <span className="domain-actions">
                    <button type="button" className="btn btn-small" onClick={() => verifyDomain(d.domain)} disabled={busy}>{d.status === "active" ? "Re-check" : "Verify"}</button>
                    {confirmId === d.domain ? (
                      <button type="button" className="btn btn-small btn-danger" onClick={() => removeDomain(d.domain)} disabled={busy}>Confirm remove</button>
                    ) : (
                      <button type="button" className="btn btn-small" onClick={() => setConfirmId(d.domain)} disabled={busy}>Remove</button>
                    )}
                  </span>
                </div>
                {d.last_error && <div className="domain-error">{d.last_error}</div>}
                {d.status !== "active" && d.dns && (
                  <div className="domain-dns">
                    {d.dns.map((r) => (
                      <div className="dns-rec" key={r.name}>
                        <span className="dns-type">{r.type}</span>
                        <code className="dns-name">{r.name}</code>
                        <CopyMini text={r.name} label="Copy record name" />
                        <span className="dns-arrow" aria-hidden="true">-&gt;</span>
                        <code className="dns-value">{r.value}</code>
                        <CopyMini text={r.value} label="Copy record value" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {showRename && <RenameDialog name={name} onClose={() => setShowRename(false)} onRenamed={onRenamed} />}
      {/* Portaled to <body>: this dialog is opened from the title bar too, but it
          lives inside the Settings pane, which is display:none unless the Settings
          tab is active -- so without the portal it stays invisible until then. */}
      {showVisibility &&
        createPortal(
          <VisibilityDialog
            app={app}
            isPrivate={!!app.private}
            viewers={viewers}
            collabs={collabs}
            allowListed={!!account.app_listing_enabled}
            onSave={saveVisibility}
            onClose={() => setShowVisibility(false)}
          />,
          document.body,
        )}
      {showResources && (
        <ResourcesDialog
          app={app}
          isAdmin={isAdmin}
          account={account}
          showToast={showToast}
          onClose={() => setShowResources(false)}
          onSaved={onSaved}
        />
      )}
      {showTransfer && (
        <TransferDialog
          name={name}
          onClose={() => setShowTransfer(false)}
          onTransferred={() => {
            setShowTransfer(false);
            showToast("Ownership transferred; you are now a collaborator");
            if (onSaved) onSaved();
            load();
          }}
        />
      )}
    </div>
  );
};

// One snapshot row's actions: three inline icons on a wide screen, collapsed into
// a single kebab menu on a phone (CSS swaps which one shows).
// The app's connections, on their own tab: granting a credential is a thing you
// come here to do, not a row you scroll past at the bottom of Settings. It loads
// its own data rather than borrowing Settings' state, so the two tabs do not
// have to be open together for either to work.
export const AppConnections = ({ name, isChat = false }) => {
  const noun = isChat ? "conversation" : "app";
  const [conns, setConns] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setConns(await api.get(`/api/apps/${encodeURIComponent(name)}/connections`));
    } catch (err) {
      setError(err.message);
      setConns({ granted: [], available: [] });
    }
  }, [name]);
  useEffect(() => {
    load();
  }, [load]);

  const onToggle = async (c, granted) => {
    setError("");
    try {
      const path = `/api/apps/${encodeURIComponent(name)}/connections/${encodeURIComponent(c.slug)}`;
      if (granted) {
        await api.del(path);
      } else {
        await api.put(path, {});
        warnIfShared(name, c.label || c.slug);
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
  <div className="ov">
    <div className="ov-hero ov-hero-lite">
      <div className="ov-id">
        <div className="ov-nm">Connections{(conns?.granted || []).length > 0 ? <span className="snap-count">{conns.granted.length}</span> : ""}</div>
        <div className="ov-desc">
          Accounts, credentials and MCP servers from your{" "}
          <Link to="/connections">Connections</Link> page. Granting one lets this {noun} act as you
          against that account -- no credential is stored, and revoking is immediate.{" "}
          <DocsLink guide="user" section="connections">How connections work</DocsLink>
        </div>
      </div>
    </div>
    <ErrorBanner message={error} onDismiss={() => setError("")} />
    <section className="ov-section">
        {conns === null ? (
          <p className="hint">Loading...</p>
        ) : (conns.granted || []).length === 0 && (conns.available || []).length === 0 ? (
          <p className="hint">
            Nothing attached yet. Connect an account or add a credential in your{" "}
            <Link to="/settings">Settings</Link> first.
          </p>
        ) : (
          <>
            {(conns.granted || []).map((c) => (
              <div key={c.slug} className="conn-row">
                <div className="conn-id">
                  <span className="conn-name">
                    <ConnIcon url={c.icon_url} className="conn-row-icon" />
                    {c.label || c.slug}
                    <span className="conn-provider">{c.provider_label}</span>
                    <span className="conn-granted-chip">Granted</span>
                  </span>
                  <span className="conn-note">
                    Identified as <span className="mono">{c.slug}</span>
                    {" -- "}
                    {c.kind === "mcp" ? (
                      <>
                        app calls its tools via the container API
                        {(c.tools || []).length > 0 && ` (${c.tools.length} tool${c.tools.length === 1 ? "" : "s"})`}
                      </>
                    ) : (
                      <>app can read the token via the container API</>
                    )}
                  </span>
                </div>
                <button type="button" className="btn btn-small" onClick={() => onToggle(c, true)}>Revoke</button>
              </div>
            ))}
            {(conns.available || []).map((c) => (
              <div key={c.slug} className="conn-row conn-ungranted">
                <div className="conn-id">
                  <span className="conn-name">
                    <ConnIcon url={c.icon_url} className="conn-row-icon" />
                    {c.label || c.slug}
                    <span className="conn-provider">{c.provider_label}</span>
                  </span>
                  <span className="conn-note">
                    Identified as <span className="mono">{c.slug}</span> -- not granted to this {noun} yet
                  </span>
                </div>
                <button type="button" className="btn btn-small btn-primary" onClick={() => onToggle(c, false)}>Grant</button>
              </div>
            ))}
          </>
        )}
      </section>
  </div>
  );
};

const SnapRowActions = ({ busy, onRollback, onFork, onDelete, exportBase, hideFork = false }) => {
  const { open, setOpen, ref } = useDropdown();
  const pick = (fn) => () => {
    setOpen(false);
    fn();
  };
  return (
    <span className="snap-tl-actions">
      <span className="snap-actions-inline">
        <button type="button" className="btn btn-small btn-icon" onClick={onRollback} disabled={busy} title="Roll back to this snapshot" aria-label="Roll back to this snapshot"><RollbackIcon /></button>
        {!hideFork && <button type="button" className="btn btn-small btn-icon" onClick={onFork} disabled={busy} title="Fork a new app from this snapshot" aria-label="Fork a new app from this snapshot"><ForkIcon /></button>}
        <DownloadMenu base={exportBase} small title="Download this snapshot" />
        <button type="button" className="btn btn-small btn-icon menu-item-danger" onClick={onDelete} disabled={busy} title="Delete this snapshot" aria-label="Delete this snapshot"><TrashIcon /></button>
      </span>
      <span className="menu snap-actions-menu" ref={ref}>
        <button type="button" className="btn btn-small btn-icon" onClick={() => setOpen(!open)} disabled={busy} aria-haspopup="menu" aria-expanded={open} aria-label="Snapshot actions"><DotsIcon /></button>
        {open && (
          <div className="menu-items" role="menu">
            <MenuItem icon={<RollbackIcon />} label="Roll back" onClick={pick(onRollback)} />
            {!hideFork && <MenuItem icon={<ForkIcon />} label="Fork app" onClick={pick(onFork)} />}
            <MenuDownloadSub base={exportBase} label="Download snapshot" />
            <div className="menu-sep" />
            <MenuItem icon={<TrashIcon />} label="Delete" onClick={pick(onDelete)} danger />
          </div>
        )}
      </span>
    </span>
  );
};

// The Snapshots header's primary action: a split button -- take a snapshot, or
// (via the caret) fork the app from its current state.
// A single rounded primary button, styled exactly like "Open app". Forking is
// available from the app header (current state) and per-snapshot in each row, so
// this no longer needs a split caret.
const SnapTakeButton = ({ onNew }) => (
  <button type="button" className="btn btn-primary" onClick={onNew}>
    Take snapshot
  </button>
);

// The Snapshots view: the snapshots list with rollback / fork / delete, and a
// "Take snapshot" action -- the former dialog's contents, inline in a tab.
export const SnapshotsPane = ({ name, showToast, onRolledBack, onFork, onNew, reloadSignal, isChat = false }) => {
  const [snaps, setSnaps] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await api.get(`/api/apps/${encodeURIComponent(name)}/snapshots`);
      setSnaps(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message);
      setSnaps([]);
    }
  }, [name]);
  useEffect(() => {
    load();
  }, [load, reloadSignal]);

  const doRollback = async () => {
    const id = confirm?.snap?.id;
    if (!id || busy) return;
    setBusy(true);
    setError("");
    showToast("Rolling back...");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/snapshots/${encodeURIComponent(id)}/restore`, {});
      setConfirm(null);
      setBusy(false);
      if (onRolledBack) onRolledBack();
    } catch (err) {
      setError(err.message);
      setBusy(false);
      setConfirm(null);
    }
  };
  const doRemove = async () => {
    const id = confirm?.snap?.id;
    if (!id || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.del(`/api/apps/${encodeURIComponent(name)}/snapshots/${encodeURIComponent(id)}`);
      showToast("Snapshot deleted");
      setConfirm(null);
      await load();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Group snapshots by calendar day (newest first) for the timeline, so the
  // history reads "Today / Yesterday / earlier" down a single rail.
  const dayLabel = (d) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
  };
  const groups = [];
  (snaps || []).forEach((s) => {
    const key = new Date(s.created_at).toDateString();
    let group = groups.length && groups[groups.length - 1].key === key ? groups[groups.length - 1] : null;
    if (!group) {
      group = { key, label: dayLabel(new Date(s.created_at)), items: [] };
      groups.push(group);
    }
    group.items.push(s);
  });

  return (
    <div className="ov">
      <div className="ov-hero ov-hero-lite">
        <div className="ov-id">
          <div className="ov-nm">Snapshots{snaps && snaps.length > 0 ? <span className="snap-count">{snaps.length}</span> : ""}</div>
          <div className="ov-desc">{isChat
            ? "Save a point-in-time copy of the assets this conversation has built, then roll back to it later (reversible -- the current state is snapshotted first). Nothing is snapshotted automatically."
            : <>hostit snapshots {name} automatically on a schedule and before every deploy. Take one yourself anytime, roll back to any point (reversible -- the current state is snapshotted first), or fork a snapshot into a brand-new app.</>}</div>
        </div>
        <div className="ov-quick">
          <SnapTakeButton onNew={() => onNew(load)} />
        </div>
      </div>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      {snaps === null ? (
        <p className="hint">Loading...</p>
      ) : snaps.length === 0 ? (
        <p className="hint">{isChat ? "No snapshots yet. Once this conversation has built some assets, take one to keep a version you can roll back to." : "No snapshots yet."}</p>
      ) : (
        <div className="snap-tl">
          {groups.map((group) => (
            <div className="snap-tl-group" key={group.key}>
              <div className="snap-tl-day">{group.label}</div>
              {group.items.map((s) => {
                const when = new Date(s.created_at);
                const isLatest = snaps[0] && snaps[0].id === s.id;
                return (
                  <div className={"snap-tl-item" + (s.auto ? "" : " manual") + (isLatest ? " latest" : "")} key={s.id}>
                    <span className="snap-tl-node" />
                    <span className="snap-tl-when">{when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="snap-tl-label">
                      <span className={"snap-kind" + (s.auto ? "" : " snap-kind-manual")}>{s.auto ? "auto" : "manual"}</span>
                      {s.label ? <span>{s.label}</span> : <span className="snap-tl-untitled">{s.auto ? "Automated snapshot" : "Manual snapshot"}</span>}
                      {isLatest && <span className="snap-tl-latest">latest</span>}
                    </span>
                    <SnapRowActions
                      busy={busy}
                      hideFork={isChat}
                      onRollback={() => setConfirm({ type: "rollback", snap: s })}
                      onFork={() => onFork(s.id)}
                      onDelete={() => setConfirm({ type: "delete", snap: s })}
                      exportBase={`/api/apps/${encodeURIComponent(name)}/snapshots/${encodeURIComponent(s.id)}/export`}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {confirm?.type === "rollback" && (
        <ConfirmDialog title="Roll back to this snapshot?" confirmLabel="Roll back" busy={busy} onConfirm={doRollback} onCancel={() => setConfirm(null)}>
          {isChat
            ? <>This conversation's assets will be restored to {new Date(confirm.snap.created_at).toLocaleString()}. A snapshot of the current state is taken first, so this is reversible.</>
            : <><span className="mono">{name}</span>'s files and installed software will be restored to {new Date(confirm.snap.created_at).toLocaleString()}. A snapshot of the current state is taken first, so this is reversible.</>}
        </ConfirmDialog>
      )}
      {confirm?.type === "delete" && (
        <ConfirmDialog title="Delete this snapshot?" confirmLabel="Delete" danger busy={busy} onConfirm={doRemove} onCancel={() => setConfirm(null)}>
          The snapshot from {new Date(confirm.snap.created_at).toLocaleString()} will be permanently deleted.
        </ConfirmDialog>
      )}
    </div>
  );
};

// TabsMenu is the owner's "View" control in the tab strip: a small dropdown that
// picks which tabs this app shows for everyone who opens it (owner and
// collaborators). It edits the per-app OVERRIDE -- with none set, the checkboxes
// reflect the EFFECTIVE default actually in force (the viewer's profile default,
// else the built-in all-tabs default), so the menu matches what is on screen; and
// "Reset to my default" clears the override so each viewer's own profile
// preference applies again.
const TabsMenu = ({ app, assistantEnabled, defaultTabs, onSetTabs }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {top,right} for the fixed popup
  const ref = useRef(null);
  const btnRef = useRef(null);
  // The tab strip scrolls horizontally, so it clips an absolutely positioned
  // popup. Position it FIXED at the button's coordinates instead, the same way
  // the new-app connection picker escapes the modal's clip.
  const openMenu = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const keys = TOGGLEABLE_TABS.filter((k) => k !== "assistant" || assistantEnabled);
  // Reflect the tabs actually in force: the app override if set, else the viewer's
  // profile default, else all -- the same resolution the tab strip renders, so the
  // menu never disagrees with what is on screen.
  const checked = new Set(resolveTabs(app.tabs, defaultTabs, assistantEnabled));
  // Assistant and Files are the two primary panes, and one must always be on.
  // A primary is LOCKED (shown checked and greyed, cannot be turned off) when it
  // is the only primary left on -- so unchecking one flips the other on and locks
  // it, and there is never a moment with neither.
  const filesOn = checked.has("files");
  const assistantOn = checked.has("assistant");
  const locked = (key) =>
    (key === "files" && filesOn && !assistantOn) || (key === "assistant" && assistantOn && !filesOn);
  const toggle = (key) => {
    if (locked(key)) return; // the sole remaining primary cannot be turned off
    const next = new Set(checked);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSetTabs(tabsToCsv(normalizeTabs([...next], assistantEnabled)));
  };
  return (
    <div className="ws-tabsmenu" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className={"btn btn-icon ws-collapsible" + (open ? " on" : "")}
        onClick={openMenu}
        aria-haspopup="true"
        aria-expanded={open}
        title="Choose which tabs this app shows"
        aria-label="Choose which tabs this app shows"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M9 4v16" />
        </svg>
      </button>
      {open && pos && (
        <div className="ws-tabsmenu-pop" role="menu" style={{ top: pos.top, right: pos.right }}>
          <p className="ws-tabsmenu-hint">Tabs shown for this app</p>
          {keys.map((key) => {
            const isLocked = locked(key);
            return (
              <button
                key={key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked.has(key)}
                disabled={isLocked}
                className={"ws-tabsmenu-item" + (checked.has(key) ? " on" : "") + (isLocked ? " locked" : "")}
                onClick={() => toggle(key)}
                title={isLocked ? "Assistant or Files must stay on" : undefined}
              >
                <span className="ws-tabsmenu-check" aria-hidden="true">{checked.has(key) ? "✓" : ""}</span>
                {TAB_LABELS[key]}
              </button>
            );
          })}
          <button type="button" className="ws-tabsmenu-reset" onClick={() => { onSetTabs(""); setOpen(false); }}>
            Reset to my default
          </button>
        </div>
      )}
    </div>
  );
};

const AppDetail = ({ account, refreshAccount }) => {
  const { name, viewSlug } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  // The visibility dialog lives in AppSettings (always mounted, so it shows as a
  // modal over any view), but its open-state is here so the title-bar badge can
  // open it as well as the settings overview row.
  const [showVisibility, setShowVisibility] = useState(false);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(false);
  // Set the moment a delete succeeds, before we navigate back to the app list.
  // The ref stops the poll loop from re-fetching a now-deleted app (a 404); the
  // state guards the render so the not-found card never flashes during the
  // redirect (see detailScreen). Both are needed: a poll already in flight when
  // the DELETE landed will still resolve and set `missing`, and only the render
  // guard can beat that.
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [pending, setPending] = useState(null); // an in-flight lifecycle transition, or null
  const [confirmDelete, setConfirmDelete] = useState(false);
  // null when no archive dialog is open, else true (archiving) or false
  // (bringing it back) -- the two need different words, not one generic prompt.
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [termOpen, setTermOpen] = useState(false); // a shell session exists (mounted)
  const [termMin, setTermMin] = useState(false); // ...but hidden out of the way
  const [termConnecting, setTermConnecting] = useState(false); // opening, not yet connected
  const termOpenRef = useRef(false);
  termOpenRef.current = termOpen;
  const [showSsh, setShowSsh] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showNewSnapshot, setShowNewSnapshot] = useState(false);
  const [showFork, setShowFork] = useState(false);
  const [showRenameApp, setShowRenameApp] = useState(false);
  const [showTransferApp, setShowTransferApp] = useState(false);
  const [showResourcesApp, setShowResourcesApp] = useState(false);
  const [forkSnapshotId, setForkSnapshotId] = useState(null); // null = fork from current state
  const [snapReload, setSnapReload] = useState(0); // bump to refresh the Snapshots pane
  const [filesReload, setFilesReload] = useState(0); // bump to re-list the Files tree
  const [hasKeys, setHasKeys] = useState(null); // null until we know, so nothing flickers
  const [toast, setToast] = useState(""); // a 3s "Copied"/"Regenerated" snackbar
  // Remember the last view per app (also seeded by the new-app intent).
  // The view lives in the URL (/app/<name>/files etc.), so every tab is deep-
  // linkable; a bare /app/<name> falls back to the per-app remembered view,
  // and an unknown slug is null -- rendered as a not-found further down, after
  // the hooks (an early return here would change the hook order).
  const view = viewFromSlug(viewSlug, rememberedView(name));
  // Has the terminal tab ever been opened on this page? Once it has, the
  // terminal stays mounted so switching away does not kill the session; before
  // it has, there is nothing to connect. See where it is rendered.
  const [termOpened, setTermOpened] = useState(view === "terminal");
  useEffect(() => {
    if (view === "terminal") setTermOpened(true);
  }, [view]);
  const setView = (v) => navigate(`/app/${encodeURIComponent(name)}/${VIEW_TO_SLUG[v] || "assistant"}`);
  useEffect(() => {
    if (view === null) {
      return; // an unknown slug must not overwrite the remembered view
    }
    try {
      localStorage.setItem("hostit.view." + name, view);
    } catch {
      /* ignore */
    }
  }, [name, view]);
  // Every tab is mounted on page load, not on first visit, so the editor,
  // terminal, logs and assistant all start rendering and loading their data
  // immediately and switching to any of them is instant. Inactive panes are
  // hidden with CSS (display:none); the terminal refits itself (ResizeObserver)
  // when its pane becomes visible. The active tab is fetched first (prefetchTabs)
  // so its chunk is not queued behind the others.
  const initialView = useRef(view);
  useEffect(() => {
    prefetchTabs(initialView.current);
  }, []);
  // Which toggleable tabs this app shows: the app's own override, else the
  // viewer's profile default, else the built-in default (all available). The
  // always-on views (snapshots/connections/settings) are not governed by this.
  const isChat = app ? app.kind === "chat" : false;
  // A conversation's canonical surface is the dedicated Chats page, not the app
  // workspace: send it there so there is one place a chat lives (and none of the
  // app chrome -- tabs, SSH, domains, deploy -- ever shows for a chat). Only a
  // deliberate /app/<chat> URL reaches here; the app grid and nav already exclude
  // chats.
  useEffect(() => {
    if (isChat) navigate(`/chats/${encodeURIComponent(name)}`, { replace: true });
  }, [isChat, name, navigate]);
  // A conversation is not a tabbed workspace: it is a chat. So it shows only the
  // Assistant, plus Connections (what it may access) and Settings (rename,
  // convert, delete) which are rendered unconditionally below. Files, Terminal,
  // Snapshots and Logs are app/serving concepts and are hidden.
  const visibleTabList = !app ? [] : isChat ? ["assistant"] : resolveTabs(app.tabs, account.default_tabs, app.assistant_enabled);
  const visibleTabs = new Set(visibleTabList);
  // If the current view's tab has been hidden (no assistant key, or a tab turned
  // off in the preference), fall through to the first visible tab rather than
  // showing an empty pane. This also covers the old "no assistant -> editor" case.
  useEffect(() => {
    if (!app) return;
    const key = VIEW_TO_TABKEY[view];
    if (key && !visibleTabs.has(key)) {
      const firstVisible = TABKEY_TO_VIEW[visibleTabList[0]] || "settings";
      setView(firstVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, view]);
  // Refresh the Snapshots pane every time it is opened, so it is never stale.
  useEffect(() => {
    if (view === "snapshots") setSnapReload((k) => k + 1);
  }, [view]);
  // Same for the Files view: re-list the tree on open so a change made from the
  // assistant, a deploy or scp while another tab showed is reflected at once.
  useEffect(() => {
    if (view === "editor") setFilesReload((k) => k + 1);
  }, [view]);
  const toastTimer = useRef(null);
  const catchUpTimers = useRef([]);

  // showToast flashes a message at the bottom for 3 seconds, then clears it.
  const showToast = useCallback((message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 3000);
  }, []);

  // setTabs writes the owner's per-app tab override (empty clears it). The PUT
  // returns the updated app, so the tab strip re-renders from it immediately.
  const setTabs = async (csv) => {
    try {
      const updated = await api.put(`/api/apps/${encodeURIComponent(name)}/tabs`, { tabs: csv });
      // Merge ONLY the tabs field, so the live state the poller keeps fresh
      // (running, app_running, usage) is not clobbered by this write's response
      // -- which would flash the app as powered off until the next poll.
      setApp((prev) => (prev ? { ...prev, tabs: updated.tabs } : updated));
      showToast(csv ? "View saved for this app" : "View reset to your default");
    } catch (err) {
      showToast(err.message);
    }
  };

  // Copy the current app token to the clipboard; regenerate mints a new one, points
  // the page at it, and copies that. Both flash a snackbar.
  const copyToken = useCallback(async () => {
    const t = app?.agent_token;
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      showToast("Copied to clipboard");
    } catch {
      showToast("Copy failed");
    }
  }, [app, showToast]);

  const regenerateToken = useCallback(async () => {
    try {
      const fresh = await api.post(`/api/apps/${encodeURIComponent(name)}/token`);
      setApp(fresh);
      try {
        await navigator.clipboard.writeText(fresh.agent_token || "");
      } catch {
        // clipboard may be unavailable; the token still rotated
      }
      showToast("Regenerated and copied");
    } catch (err) {
      setError(err.message);
    }
  }, [name, showToast]);

  // The live preview reloads whenever the app is (re)deployed by anything -- the
  // chat's deploy tool, an external `hostit deploy`, a restart. We remount the
  // iframe by bumping its key, and flag "refreshing" until it loads again.
  // Seed the key with a timestamp (not 0) so preview URLs are unique per session
  // and never reuse a value a cache might still hold; increments keep it changing.
  const [previewKey, setPreviewKey] = useState(() => Date.now());
  // ONE preview-visibility state for the assistant AND files views (the strip
  // has one toggle; the panes must agree), remembered per app across sessions.
  // Default: visible, except on narrow screens where the pane would crowd out
  // the work surface.
  const [previewOn, setPreviewOn] = useState(() => {
    try {
      const saved = localStorage.getItem("hostit.preview." + name);
      if (saved !== null) return saved === "1";
    } catch {
      /* storage unavailable */
    }
    return !(typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches);
  });
  useEffect(() => {
    try {
      localStorage.setItem("hostit.preview." + name, previewOn ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
  }, [name, previewOn]);
  // The files view's preview pane, controlled by the same strip buttons; the
  // editor only receives the state (one set of controls for both views).
  const [filesPreviewNonce, setFilesPreviewNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const lastAppStart = useRef(null);
  const refreshTimer = useRef(null);

  const reloadPreview = useCallback(() => {
    // Always freshen the URL so the preview is current whenever it is next shown.
    setPreviewKey((k) => k + 1);
    // A (re)deploy takes an automatic snapshot, so refresh the Snapshots pane too.
    setSnapReload((k) => k + 1);
    // On small screens the preview is hidden: there is no pane to spin over, and a
    // hidden lazy iframe never fires onLoad, so skip the "refreshing" status there.
    if (window.matchMedia("(max-width: 820px)").matches) {
      return;
    }
    setRefreshing(true);
    clearTimeout(refreshTimer.current);
    // Fallback: some apps never fire load on the iframe; do not spin forever.
    refreshTimer.current = setTimeout(() => setRefreshing(false), 6000);
  }, []);

  // Open the web shell, or bring back a minimized session. The terminal stays
  // mounted while minimized so the shell keeps running, so only a fresh open (not
  // a restore) shows the "connecting" pulse.
  const openWebShell = useCallback(() => {
    if (!termOpenRef.current) {
      setTermConnecting(true);
    }
    setTermOpen(true);
    setTermMin(false);
  }, []);

  // Tear the terminal down: on the user's close, and when the server ends the
  // session (a reboot/poweroff), so the button stops showing a live session.
  const closeTerminal = useCallback(() => {
    setTermOpen(false);
    setTermMin(false);
    setTermConnecting(false);
  }, []);

  // Publish this app's identity to the nav, so on phones it can show the back
  // button and name in place of the logo (a single top bar). Clear it on the way
  // out.
  const setAppHeader = useSetAppHeader();
  useEffect(() => {
    if (app) {
      setAppHeader({ name: app.name, running: app.running, appRunning: app.app_running, appState: app.app_state, pending: !!pending || refreshing, archived: !!app.archived });
    }
  }, [app, pending, refreshing, setAppHeader]);
  useEffect(() => () => setAppHeader(null), [setAppHeader]);

  // The chat/preview divider position, remembered across reloads.
  const [chatFrac, setChatFrac] = useState(() => {
    const saved = parseFloat(localStorage.getItem(splitKey) || "");
    return saved >= 0.2 && saved <= 0.8 ? saved : defaultChatFrac;
  });
  const chatFracRef = useRef(chatFrac);
  chatFracRef.current = chatFrac;
  const splitRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    let latest = chatFracRef.current; // track here; React state lags a frame behind
    const move = (ev) => {
      const rect = splitRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      latest = Math.min(0.8, Math.max(0.2, (clientX - rect.left) / rect.width));
      setChatFrac(latest);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("ws-resizing");
      localStorage.setItem(splitKey, String(latest));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    document.body.classList.add("ws-resizing");
  };

  // Whether SSH is usable at all depends on the profile, not on the app
  useEffect(() => {
    api
      .get("/api/account/keys")
      .then((keys) => setHasKeys(keys.length > 0))
      .catch(() => setHasKeys(null));
  }, []);

  const load = useCallback(async () => {
    if (deletingRef.current) return; // deleted; a redirect is in flight, don't re-fetch a 404
    if (!navigator.onLine) return; // offline: don't hammer, wait for reconnect
    try {
      const fresh = await api.get(`/api/apps/${encodeURIComponent(name)}`);
      setApp(fresh);
      setMissing(false);
      setError(""); // a good read heals any transient error banner
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setMissing(true);
      } else if (isNetworkError(err)) {
        // A transient network blip -- the app or daemon restarting, a wifi hiccup.
        // Keep showing the last known state and let the next poll recover, rather
        // than flashing a scary "Network error" banner on every restart.
      } else {
        setError(err.message);
      }
    }
  }, [name]);

  // A container takes a few seconds to come up, so a single reload right after
  // landing (or after start/restart) catches it mid-boot and shows a red dot for
  // an app that is fine. Look again a few times while it settles.
  const scheduleCatchUp = useCallback(() => {
    catchUpTimers.current.forEach(clearTimeout);
    catchUpTimers.current = [5, 10, 15].map((seconds) => setTimeout(load, seconds * 1000));
  }, [load]);

  useEffect(() => {
    load();
    scheduleCatchUp();
    const ticker = setInterval(load, detailPollMs);
    return () => {
      catchUpTimers.current.forEach(clearTimeout);
      clearInterval(ticker);
      clearTimeout(refreshTimer.current);
    };
  }, [load, scheduleCatchUp]);
  // The toast timer is cleared only on unmount, NOT in the poll effect above: a
  // rename changes `name` -> `load` -> re-runs that effect, which would otherwise
  // clear the just-set "App renamed" timer and leave the snackbar stuck forever.
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  // Reflect the current app in the tab title.
  useEffect(() => {
    document.title = name ? `${name} - hostit` : "hostit";
    return () => {
      document.title = "hostit";
    };
  }, [name]);
  useReconnect(load); // refresh when connectivity or visibility returns

  // The app process restarting (a deploy, restart or reboot) is our signal to
  // reload the preview: whatever changed is now live. Skip the very first read,
  // which only establishes the baseline.
  useEffect(() => {
    if (!app) return;
    const startedAt = app.app_started_at || 0;
    if (lastAppStart.current === null) {
      lastAppStart.current = startedAt;
      return;
    }
    if (startedAt !== lastAppStart.current) {
      lastAppStart.current = startedAt;
      if (app.running) {
        reloadPreview();
      }
    }
  }, [app, reloadPreview]);

  // Lifecycle runs through the agent API, which takes our session cookie just like
  // the REST API does. We do not guess the result: we record the app's start times
  // at the moment of the click and then poll until the app has actually reached the
  // target state. That is the only way to show a reboot or an app restart honestly,
  // since both end in the same running state they began in -- "done" means a start
  // time newer than the one we saw, not merely "running" again.
  // Archiving is not a lifecycle verb: it changes what the app IS rather than
  // what it is doing, so it does not go through the pending/transition machinery
  // (there is no container state to wait for).
  const setArchived = async (archived) => {
    setError("");
    setArchiving(true);
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/${archived ? "archive" : "unarchive"}`);
      await load();
      setConfirmArchive(null);
    } catch (err) {
      setError(err.message);
      setConfirmArchive(null);
    } finally {
      setArchiving(false);
    }
  };

  const lifecycle = async (action) => {
    if (pending) {
      return;
    }
    const transition = TRANSITIONS[action];
    const base = { started_at: app?.started_at || 0, app_started_at: app?.app_started_at || 0 };
    setError("");
    setPending({ verb: action, label: transition.label, base, since: Date.now() });
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/${action}`);
    } catch (err) {
      // The action itself was refused (e.g. an app verb while the container is
      // down): there is nothing to wait for, so stop and surface it.
      setError(err.message);
      setPending(null);
    }
  };

  // Convert this conversation to an app (or back). Nothing rebuilds -- the
  // container and history stay -- so it is a quick round-trip; the assistant
  // pane reloads to pick up the new chrome.
  const [converting, setConverting] = useState(false);
  const convert = async (toKind) => {
    if (converting) return;
    setConverting(true);
    setError("");
    try {
      await api.post(`/api/apps/${encodeURIComponent(name)}/convert`, { kind: toKind });
      await load();
      if (toKind === "") {
        // Became an app: send them to its assistant, now with the app chrome.
        showToast("Converted to an app.");
      } else {
        showToast("Converted to a conversation.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  };

  // While a transition is pending, poll every couple of seconds until the app
  // reaches the target state (or we give up).  // While a transition is pending, poll every couple of seconds until the app
  // reaches the target state (or we give up). The daemon keeps its state cache
  // warm for the whole settle window, so each poll sees real progress.
  useEffect(() => {
    if (!pending) {
      return undefined;
    }
    const done = TRANSITIONS[pending.verb].done;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const fresh = await api.get(`/api/apps/${encodeURIComponent(name)}`);
        if (cancelled) {
          return;
        }
        setApp(fresh);
        if (done(fresh, pending.base)) {
          setPending(null);
          return;
        }
      } catch {
        // Transient error mid-transition: keep polling.
      }
      if (cancelled) {
        return;
      }
      if (Date.now() - pending.since > transitionTimeoutMs) {
        setPending(null);
        setError(`${pending.label} did not finish in time; showing the last known state`);
        return;
      }
      timer = setTimeout(poll, transitionPollMs);
    };
    timer = setTimeout(poll, transitionPollMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pending, name]);

  const deleted = () => {
    // Stop polling and lock the render into the "deleting" screen before we
    // redirect, so an in-flight 404 poll can't flash the not-found card.
    deletingRef.current = true;
    setDeleting(true);
    refreshAccount();
    navigate("/", { replace: true });
  };

  const screen = detailScreen({ deleting, missing, view, error, app });
  if (screen === "deleting") {
    return <Loading label="Deleting app..." />;
  }
  if (screen === "notfound") {
    return <NotFound name={name} />;
  }
  if (screen === "unknown") {
    return <UnknownView name={name} slug={viewSlug} />;
  }
  if (screen === "error") {
    return (
      <>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <Link className="btn" to="/">
          Back to apps
        </Link>
      </>
    );
  }
  if (screen === "loading") {
    return <Loading label="Loading app..." />;
  }

  const own = !account.limits || app.owner_email === undefined || app.owner_email === account.email;
  const token = app.agent_token || "";
  const prompt = promptText(app.name, app.url, token || tokenPlaceholder, Boolean((app.description || "").trim()));
  const infoUrl = `${origin}/api/apps/${app.name}/info`;

  // A cache-busting query on the preview URL, bumped on every reload, so a refresh
  // always fetches the live app rather than the browser's cached copy.
  const previewSrc = app.url + (app.url.includes("?") ? "&" : "?") + "hostit_preview=" + previewKey;

  // A crash loop that gave up: container up, but the app failed repeatedly and
  // hostit stopped restarting it. Worth calling out in red, not the neutral amber.
  const crashed = app.running && app.app_state === "failed";
  // "Running" is the unremarkable state, so it is left unsaid; only the states
  // worth noticing get a label.
  const statusText = pending
    ? null
    : refreshing
      ? "Refreshing preview"
      : app.archived
        ? "Archived"
        : !app.running
          ? "Powered off"
          : crashed
            ? "App crashed"
            : app.app_running
              ? ""
              : "App stopped";
  const statusPending = !!pending || refreshing;

  return (
    <>
      <div className={"ws-page" + (view === "settings" || view === "snapshots" || view === "logs" || view === "connections" ? " ws-doc" : "")}>
        {/* Top bar. Left: identity (the "Running" state is left unsaid -- only the
            notable states are named). Right: the live resources beside the
            controls, all vertically centred. */}
        <div className="ws-topbar">
          <Link to="/" className="ws-back" aria-label="Back to apps" title="Back to apps">
            <BackIcon />
          </Link>
          <div className="ws-idrow">
            <span className="ws-name">{app.name}</span>
            <StatusDot running={app.running} appRunning={app.app_running} appState={app.app_state} pending={statusPending} archived={!!app.archived} />
            {pending ? (
              <span className="status-label status-label-pending">
                {pending.label}
                <span className="ellipsis" aria-hidden="true">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            ) : (
              statusText && (
                <span className={"status-label" + (refreshing ? " status-label-pending" : "") + (crashed ? " status-label-crashed" : "")}>{statusText}</span>
              )
            )}
            {/* A conversation has no public/private serving concept -- it never
                serves -- so its visibility badge is hidden. */}
            {!isChat && (app.is_owner ? (
              <button
                type="button"
                className="vis-badge-btn"
                onClick={() => setShowVisibility(true)}
                title="Change who can see this app"
              >
                <VisibilityBadge state={visibilityOf(!!app.private, app.viewer_count, !!app.listed)} />
              </button>
            ) : (
              <VisibilityBadge state={visibilityOf(!!app.private, app.viewer_count, !!app.listed)} />
            ))}
            {isChat && <span className="ws-chat-badge">Conversation</span>}
          </div>

          <div className="ws-topright">
            {app.running && <UsageGrid app={app} />}
            <div className="ws-topacts">
              {/* A conversation is a chat, not a workspace: the tab customizer,
                  SSH, bring-your-own-agent, workspace export and fork are all
                  app tools and are hidden. The Actions menu (rename, convert,
                  delete, power) stays. */}
              {!isChat && app.is_owner && (
                <TabsMenu app={app} assistantEnabled={app.assistant_enabled} defaultTabs={account.default_tabs} onSetTabs={setTabs} />
              )}
              {!isChat && (
                <button
                  type="button"
                  className="btn btn-icon ws-collapsible"
                  onClick={() => setShowSsh(true)}
                  title="Connect via SSH"
                  aria-label="Connect via SSH"
                >
                  <SshMenuIcon />
                </button>
              )}
              {!isChat && (
                <button
                  type="button"
                  className="btn btn-icon btn-sparkle ws-collapsible"
                  onClick={() => setShowPrompt(true)}
                  title="Use your own AI agent"
                  aria-label="Use your own AI agent"
                >
                  <SparkleIcon />
                </button>
              )}
              {!isChat && (
                <DownloadMenu
                  base={`/api/apps/${encodeURIComponent(app.name)}/export`}
                  className="ws-collapsible header-dl"
                  title="Download workspace"
                />
              )}
              {!isChat && (
                <button
                  type="button"
                  className="btn btn-icon ws-collapsible"
                  onClick={() => {
                    setForkSnapshotId(null);
                    setShowFork(true);
                  }}
                  title="Fork app"
                  aria-label="Fork app"
                >
                  <ForkIcon />
                </button>
              )}
              <ActionsMenu
                running={app.running}
                appRunning={app.app_running}
                busy={!!pending}
                onAction={lifecycle}
                onDelete={() => setConfirmDelete(true)}
                canDelete={!!app.is_owner}
                onSsh={() => setShowSsh(true)}
                onByoa={() => setShowPrompt(true)}
                archived={!!app.archived}
                onArchive={() => setConfirmArchive(true)}
                onUnarchive={() => setConfirmArchive(false)}
                onFork={() => {
                  setForkSnapshotId(null);
                  setShowFork(true);
                }}
                downloadBase={`/api/apps/${encodeURIComponent(app.name)}/export`}
                onRename={() => setShowRenameApp(true)}
                onTransfer={() => setShowTransferApp(true)}
                onResources={() => setShowResourcesApp(true)}
                isChat={isChat}
                onConvert={() => convert(isChat ? "" : "chat")}
              />
              {showRenameApp && (
                <RenameDialog
                  name={app.name}
                  onClose={() => setShowRenameApp(false)}
                  onRenamed={(updated) => {
                    setShowRenameApp(false);
                    showToast("App renamed");
                    navigate(`/app/${encodeURIComponent(updated.name)}`, { replace: true });
                  }}
                />
              )}
              {showTransferApp && (
                <TransferDialog
                  name={app.name}
                  onClose={() => setShowTransferApp(false)}
                  onTransferred={() => {
                    setShowTransferApp(false);
                    showToast("Ownership transferred");
                    load();
                  }}
                />
              )}
              {showResourcesApp && (
                <ResourcesDialog
                  app={app}
                  isAdmin={account.role === "admin"}
                  account={account}
                  showToast={showToast}
                  onClose={() => setShowResourcesApp(false)}
                  onSaved={load}
                />
              )}
              {!isChat && (
                <a className="btn btn-primary ws-open" href={app.custom_domain ? `https://${app.custom_domain}` : app.url} target="_blank" rel="noreferrer" title="Open app">
                  <span className="ws-open-label">Open app</span>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6.5 3.5H3.5v9h9V9.5" />
                    <path d="M9.5 3.5H12.5V6.5" />
                    <path d="M12.5 3.5 7.5 8.5" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
        <ErrorBanner message={error} onDismiss={() => setError("")} />

        {/* View switcher: the chat + preview split is one view; the file editor is
            another. More can join as tabs (terminal, details) later. */}
        <div className="ws-viewtabs" role="tablist" aria-label="Workspace view">
          {visibleTabs.has("assistant") && (
            <button
              type="button"
              role="tab"
              aria-selected={view === "assistant"}
              className={"ws-viewtab" + (view === "assistant" ? " on" : "")}
              onClick={() => setView("assistant")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Assistant
            </button>
          )}
          {visibleTabs.has("files") && (
            <button
              type="button"
              role="tab"
              aria-selected={view === "editor"}
              className={"ws-viewtab" + (view === "editor" ? " on" : "")}
              onClick={() => setView("editor")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
              Files
            </button>
          )}
          {visibleTabs.has("terminal") && (
            <button
              type="button"
              role="tab"
              aria-selected={view === "terminal"}
              className={"ws-viewtab" + (view === "terminal" ? " on" : "")}
              onClick={() => setView("terminal")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M4 5h16v14H4z" />
                <path d="m8 10 3 2-3 2M13 16h4" />
              </svg>
              Terminal
            </button>
          )}
          {app.snapshots_enabled && !isChat && (
            <button
              type="button"
              role="tab"
              aria-selected={view === "snapshots"}
              className={"ws-viewtab" + (view === "snapshots" ? " on" : "")}
              onClick={() => setView("snapshots")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                {/* Counter-clockwise arc with its arrowhead at the arc's end, and the clock's hands */}
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l3 2" />
              </svg>
              Snapshots
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={view === "connections"}
            className={"ws-viewtab" + (view === "connections" ? " on" : "")}
            onClick={() => setView("connections")}
          >
            {/* A chain LINKED, not broken. The old one put its two halves on
                one line and the bar joining them on another, so the links did
                not meet the bar and it read as a chain that had snapped -- the
                opposite of what this tab is for. */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 7h3a5 5 0 0 1 0 10h-3M9 7H6a5 5 0 0 0 0 10h3" />
              <path d="M8 12h8" />
            </svg>
            Connections
          </button>
          {visibleTabs.has("logs") && (
            <button
              type="button"
              role="tab"
              aria-selected={view === "logs"}
              className={"ws-viewtab" + (view === "logs" ? " on" : "")}
              onClick={() => setView("logs")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" />
                <path d="M8 8h8M8 12h8M8 16h5" />
              </svg>
              Logs
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={view === "settings"}
            className={"ws-viewtab" + (view === "settings" ? " on" : "")}
            onClick={() => setView("settings")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.5-2.3 1a7 7 0 0 0-1.7-1l-.3-2.5h-4l-.3 2.5a7 7 0 0 0-1.7 1l-2.3-1-2 3.5 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.5 2.3-1a7 7 0 0 0 1.7 1l.3 2.5h4l.3-2.5a7 7 0 0 0 1.7-1l2.3 1 2-3.5-2-1.5a7 7 0 0 0 .1-1z" />
            </svg>
            Settings
          </button>
          {/* Preview controls: only meaningful in the assistant view, right-aligned. */}
          {!isChat && ((app.assistant_enabled && view === "assistant") || view === "editor") && (
            <div className="ws-viewtabs-right">
              {/* Hidden rather than disabled with the preview off: there is no
                  preview to refresh, so the control has nothing to explain. */}
              {previewOn && (
              <button
                type="button"
                className="ws-viewtab ws-preview-ctl"
                onClick={view === "assistant" ? reloadPreview : () => setFilesPreviewNonce((n) => n + 1)}
                title="Refresh the preview"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M20 11a8 8 0 1 0-.6 4" />
                  <path d="M20 5v6h-6" />
                </svg>
                Refresh
              </button>
              )}
              <button
                type="button"
                className={"ws-viewtab ws-preview-ctl" + (previewOn ? " on" : "")}
                onClick={() => setPreviewOn((v) => !v)}
                aria-pressed={previewOn}
                title={previewOn ? "Hide the preview" : "Show the preview"}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
                {previewOn ? "Hide preview" : "Show preview"}
              </button>
            </div>
          )}
        </div>

        {/* Views stay mounted -- only the active one is shown -- so switching is
            instant and the terminal keeps its live session (and the assistant its
            scroll) instead of reconnecting on every tab click. */}
        {app.snapshots_enabled && (
          <div className={"ws-snapshotswrap" + (view === "snapshots" ? "" : " ws-inactive")}>
            <SnapshotsPane
              name={app.name}
              showToast={showToast}
              reloadSignal={snapReload}
              onRolledBack={load}
              onNew={() => setShowNewSnapshot(true)}
              onFork={(snapshotId) => {
                setForkSnapshotId(snapshotId);
                setShowFork(true);
              }}
            />
          </div>
        )}
        <div className={"ws-connectionswrap" + (view === "connections" ? "" : " ws-inactive")}>
          <AppConnections name={app.name} />
        </div>
        <div className={"ws-logswrap" + (view === "logs" ? "" : " ws-inactive")}>
          <Suspense fallback={<div className="ws-chat-loading">Loading logs...</div>}>
            <AppLogs name={app.name} active={view === "logs"} />
          </Suspense>
        </div>
        <div className={"ws-settingswrap" + (view === "settings" ? "" : " ws-inactive")}>
          <AppSettings
            app={app}
            isAdmin={account.role === "admin"}
            account={account}
            showToast={showToast}
            hasToken={!!token}
            onCopyToken={copyToken}
            onRegenerateToken={regenerateToken}
            onSaved={load}
            onConfigureKeys={() => setShowSsh(true)}
            showVisibility={showVisibility}
            setShowVisibility={setShowVisibility}
          />
        </div>
        <div className={"ws-editorwrap" + (view === "editor" ? "" : " ws-inactive")}>
          <Suspense fallback={<div className="ws-chat-loading">Loading editor...</div>}>
            <AppEditor name={app.name} url={app.url} running={app.running} diskMB={app.disk_mb} diskLimitMB={app.disk_limit_mb} onDeploy={reloadPreview} previewOn={previewOn} previewNonce={filesPreviewNonce} reloadSignal={filesReload} />
          </Suspense>
        </div>
        <div className={"ws-termwrap" + (view === "terminal" ? "" : " ws-inactive")}>
          {/* Mounted only once the tab has been OPENED, and kept mounted after,
              so switching away does not kill the session. It used to mount with
              the page, which opened a WebSocket for everyone who never went
              near the terminal -- and left one dialling a name that no longer
              existed after a rename or a delete, logging a handshake error at
              whoever happened to be looking. */}
          {termOpened && (
            <Suspense fallback={<div className="ws-chat-loading">Loading terminal...</div>}>
              <AppTerminal name={app.name} embedded active={view === "terminal"} onSsh={() => setShowSsh(true)} />
            </Suspense>
          )}
        </div>
        {/* The assistant view (chat + preview) exists only when an Anthropic key
            is configured; otherwise the workspace opens straight into the editor. */}
        {app.assistant_enabled && (
          <div
            className={"ws-split" + (view === "assistant" ? "" : " ws-inactive")}
            ref={splitRef}
            style={{ gridTemplateColumns: (!previewOn || isChat) ? "minmax(0, 1fr)" : `minmax(0, ${chatFrac}fr) 10px minmax(0, ${1 - chatFrac}fr)` }}
          >
          <div className={"ws-chat" + (isChat ? " ws-chat-solo" : "")}>
            <Suspense fallback={<div className="ws-chat-loading">Loading assistant...</div>}>
              <AppAssistant name={app.name} embedded isChat={isChat} onPreviewRefresh={reloadPreview} onUseOwnAgent={() => setShowPrompt(true)} onOpenConnections={() => setView("connections")} />
            </Suspense>
          </div>

          {previewOn && !isChat && (
            <>
          <div
            className="ws-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat and preview"
            onPointerDown={startResize}
            onTouchStart={startResize}
          >
            <span className="ws-resizer-grip" aria-hidden="true" />
          </div>

          <div className="ws-right">
            {app.running ? (
              <div className="ws-preview">
                {refreshing && (
                  <div className="ws-preview-badge">
                    <span className="ws-preview-spinner" aria-hidden="true" /> Refreshing
                  </div>
                )}
                {/* The app runs in its own origin; sandbox keeps it from navigating
                    the dashboard away or opening windows. A per-reload cache-buster
                    on the URL forces a real fetch: Go's file server sets no
                    Cache-Control, so a plain reload would serve the browser's stale
                    copy and a "refresh" would appear to do nothing (or revert). */}
                <iframe
                  key={previewKey}
                  title={`Live preview of ${app.name}`}
                  src={previewSrc}
                  loading="lazy"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  onLoad={() => setRefreshing(false)}
                />
              </div>
            ) : (
              <div className="ws-preview ws-preview-off">
                <p>
                  The app is powered off. Use <strong>Actions</strong> to power it on, then it shows here.
                </p>
              </div>
            )}
          </div>
            </>
          )}
        </div>
        )}
      </div>

      {showSsh && <SshDialog app={app} hasKeys={hasKeys} onClose={() => setShowSsh(false)} />}
      {showPrompt && <PromptDialog prompt={prompt} infoUrl={infoUrl} token={token || tokenPlaceholder} onClose={() => setShowPrompt(false)} showToast={showToast} />}
      {showNewSnapshot && (
        <NewSnapshotDialog
          name={app.name}
          onClose={() => {
            setShowNewSnapshot(false);
            setSnapReload((k) => k + 1); // refresh the Snapshots pane once the dialog closes
          }}
          showToast={showToast}
        />
      )}
      {showFork && (
        <ForkDialog
          name={app.name}
          snapshotId={forkSnapshotId}
          onClose={() => setShowFork(false)}
          onForked={(created) => {
            setShowFork(false);
            navigate(`/app/${encodeURIComponent(created.name)}`);
          }}
        />
      )}
      {confirmDelete && (
        <DeleteAppDialog name={app.name} onCancel={() => setConfirmDelete(false)} onDeleted={deleted} />
      )}
      {confirmArchive === true && (
        <ConfirmDialog
          title="Archive this app?"
          confirmLabel="Archive"
          busy={archiving}
          onConfirm={() => setArchived(true)}
          onCancel={() => setConfirmArchive(null)}
        >
          <p>
            <b>{app.name}</b> will be powered off and kept off. It cannot be
            powered on, deployed to, or started by an SSH login until you
            unarchive it, and <b>{app.url.replace(/^https?:\/\//, "")}</b> will
            stop serving.
          </p>
          <p>
            It stops taking new snapshots, since it can no longer change. The
            ones it has keep thinning out, but a monthly snapshot is kept for a
            year and the most recent one is never removed -- so you can bring it
            back later.
          </p>
          <p>Nothing is deleted, and unarchiving is a click away.</p>
        </ConfirmDialog>
      )}
      {confirmArchive === false && (
        <ConfirmDialog
          title="Unarchive this app?"
          confirmLabel="Unarchive"
          busy={archiving}
          onConfirm={() => setArchived(false)}
          onCancel={() => setConfirmArchive(null)}
        >
          <p>
            <b>{app.name}</b> becomes an ordinary powered-off app: you can power
            it on, deploy to it, and it resumes automatic snapshots.
          </p>
          <p>It is not started for you -- power it on when you want it back.</p>
        </ConfirmDialog>
      )}
      {toast && (
        <div className="snackbar" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </>
  );
};

export default AppDetail;
